import React, { useState, useEffect, useRef } from 'react';
import { FiCopy, FiCheck, FiArrowLeft, FiRefreshCw, FiTrendingUp, FiCreditCard, FiArrowDownLeft, FiArrowUpRight, FiRepeat, FiX, FiClock, FiEye, FiEyeOff, FiChevronRight, FiChevronDown, FiClipboard, FiSearch, FiSliders, FiSettings, FiGlobe, FiCheckCircle, FiGrid, FiBell, FiInfo, FiMapPin, FiSend } from "react-icons/fi";
import { QRCodeSVG } from 'qrcode.react';
import TradeChart from '../components/TradeChart';
import TradePanel from '../components/TradePanel';
import { URL_API } from '../config/endpoints';
import { useLang, LANGS } from '../i18n';
import { useSettings, TIMEZONES } from '../settings';
import { getTgUserId, getTgInitData } from '../tgUser';

// Сети для выбора (иконки — из монетных). id используется как значение network.
const NETWORKS = [
  { id: 'BTC', name: 'Bitcoin', icon: 'btc' },
  { id: 'LIGHTNING', name: 'Lightning Network', icon: 'btc' },
  { id: 'ERC20', name: 'Ethereum (ERC20)', icon: 'eth' },
  { id: 'ARBITRUM', name: 'Arbitrum One', icon: 'arb' },
  { id: 'BASE', name: 'Base', icon: 'eth', iconUrl: '/access/coinIcon/base.svg' },
  { id: 'TRC20', name: 'Tron (TRC20)', icon: 'trx' },
  { id: 'BEP20', name: 'BNB Smart Chain (BEP20)', icon: 'bnb' },
  { id: 'POLYGON', name: 'Polygon', icon: 'pol' },
  { id: 'SOLANA', name: 'Solana', icon: 'sol' },
  { id: 'AVAXC', name: 'Avalanche C-Chain', icon: 'avax' },
  { id: 'TON', name: 'TON', icon: 'ton' },
  { id: 'XRPL', name: 'XRP Ledger', icon: 'xrp' },
  { id: 'CARDANO', name: 'Cardano', icon: 'ada' },
  { id: 'POLKADOT', name: 'Polkadot', icon: 'dot' },
  { id: 'LITECOIN', name: 'Litecoin', icon: 'ltc' },
  { id: 'COSMOS', name: 'Cosmos', icon: 'atom' },
  { id: 'APTOS', name: 'Aptos', icon: 'apt' },
  { id: 'SUI', name: 'Sui', icon: 'sui' },
  { id: 'ALGORAND', name: 'Algorand', icon: 'algo' },
  { id: 'STELLAR', name: 'Stellar', icon: 'xlm' },
  { id: 'INJECTIVE', name: 'Injective', icon: 'inj' },
  { id: 'KASPA', name: 'Kaspa', icon: 'kas' },
  { id: 'MANTLE', name: 'Mantle', icon: 'mnt' },
  { id: 'MONERO', name: 'Monero', icon: 'xmr' },
  { id: 'HYPERLIQUID', name: 'Hyperliquid', icon: 'hype' },
];
const networkById = (id) => NETWORKS.find((n) => n.id === id) || null;

// Сети, доступные для каждого токена (как на биржах). Если токена нет — фолбэк.
const TOKEN_NETWORKS = {
  BTC: ['BTC', 'LIGHTNING', 'BEP20'],
  ETH: ['ERC20', 'ARBITRUM', 'BASE', 'BEP20'],
  USDT: ['TRC20', 'ERC20', 'BEP20', 'SOLANA', 'POLYGON', 'AVAXC', 'ARBITRUM', 'TON', 'APTOS'],
  DAI: ['ERC20', 'BEP20', 'POLYGON', 'ARBITRUM'],
  BNB: ['BEP20'],
  SOL: ['SOLANA'],
  TON: ['TON'],
  TRX: ['TRC20'],
  XRP: ['XRPL'],
  ADA: ['CARDANO'],
  DOT: ['POLKADOT'],
  LTC: ['LITECOIN'],
  ATOM: ['COSMOS'],
  APT: ['APTOS'],
  SUI: ['SUI'],
  ALGO: ['ALGORAND'],
  XLM: ['STELLAR'],
  AVAX: ['AVAXC', 'BEP20'],
  POL: ['POLYGON', 'ERC20', 'BEP20'],
  ARB: ['ARBITRUM', 'ERC20'],
  INJ: ['INJECTIVE', 'ERC20', 'BEP20'],
  KAS: ['KASPA'],
  MNT: ['MANTLE', 'ERC20'],
  XMR: ['MONERO'],
  HYPE: ['HYPERLIQUID'],
  SHIB: ['ERC20', 'BEP20'],
  UNI: ['ERC20', 'ARBITRUM', 'POLYGON', 'BEP20'],
  AAVE: ['ERC20', 'POLYGON', 'ARBITRUM'],
  LINK: ['ERC20', 'BEP20', 'ARBITRUM', 'POLYGON'],
  CRV: ['ERC20', 'ARBITRUM', 'POLYGON'],
  FET: ['ERC20', 'BEP20'],
  GALA: ['ERC20', 'BEP20'],
  JUP: ['SOLANA'],
  ZORA: ['BASE', 'ERC20'],
  AXL: ['COSMOS', 'ERC20'],
  DYDYX: ['ERC20', 'COSMOS'],
};
const networksForToken = (sym) => {
  const list = TOKEN_NETWORKS[String(sym || '').toUpperCase()];
  return (list && list.length) ? list : ['ERC20', 'BEP20'];
};
// URL иконки сети: кастомная (iconUrl) или из монетных
const netIcon = (n) => (n && n.iconUrl) ? n.iconUrl : (n ? `/access/coinIcon/${n.icon}.png` : '');

// Helper to map token symbol -> local icon under public/access/coinIcon/<symbol>.png
const iconPath = (symbol) => `/access/coinIcon/${String(symbol || '').toLowerCase()}.png`;

/**
 * Custom hook to animate a numeric value from its previous state to a new target.
 * @param {number} target The target number to count to.
 * @param {number} duration Duration of the animation in seconds.
 * @returns {number} The animated number.
 */

function formatNumber(value) {
  const num = parseFloat(value);
  if (isNaN(num) || num === 0) return '0';

  const abs = Math.abs(num);

  if (abs >= 10) {
    // Large numbers — 2 decimal places
    return num.toFixed(2);
  } else if (abs >= 1) {
    // Medium — up to 4 decimal places, remove trailing zeros
    return num
      .toFixed(4)
      .replace(/\.?0+$/, '');
  } else {
    // Small — show up to 6 significant digits, remove trailing zeros
    let str = num.toPrecision(6);
    str = str.replace(/(\.\d*?[1-9])0+$/, '$1');
    str = str.replace(/\.0+$/, '');
    return str;
  }
}

// Format fee - show normal decimal numbers even for very small values
function formatFee(value) {
  const num = parseFloat(value);
  if (isNaN(num) || num === 0) return '0';
  
  const abs = Math.abs(num);
  
  // For very small numbers, show up to 8 decimal places without scientific notation
  if (abs < 1) {
    // Count significant digits - find first non-zero digit
    if (abs < 0.000001) {
      // For extremely small numbers, show 10 decimal places
      let str = num.toFixed(10);
      // Remove trailing zeros
      str = str.replace(/\.?0+$/, '');
      return str;
    } else if (abs < 0.00001) {
      // For very small numbers, show 8 decimal places
      let str = num.toFixed(8);
      str = str.replace(/\.?0+$/, '');
      return str;
    } else if (abs < 0.0001) {
      // For small numbers, show 7 decimal places
      let str = num.toFixed(7);
      str = str.replace(/\.?0+$/, '');
      return str;
    } else {
      // For medium small numbers, show 6 decimal places
      let str = num.toFixed(6);
      str = str.replace(/\.?0+$/, '');
      return str;
    }
  } else {
    return formatNumber(value);
  }
}


// Скелетон для Swap формы
// ── Единый стиль раскрывающихся панелей монет. Эталон — выпадашка Total Balance
// (редизайн 27.06): градиентное тёмное стекло + blur, мягкая аква-обводка, строки
// без рамок, тонкие разделители. Применяется к выпадашкам выбора монет (Swap,
// Deposit, Withdraw, Transfer) и шторке монеты — чтобы всё «раскрытие монет» было единым.
const GLASS_BG = 'radial-gradient(120% 75% at 82% 0%, rgba(96,189,255,0.12), transparent 58%), linear-gradient(180deg, rgba(32,34,42,0.94) 0%, rgba(18,20,26,0.96) 100%)';
const GLASS_PANEL = {
  background: GLASS_BG,
  backdropFilter: 'blur(26px)',
  WebkitBackdropFilter: 'blur(26px)',
  border: '1px solid rgba(96,189,255,0.12)',
  boxShadow: '0 18px 44px rgba(0,0,0,0.45)',
};
const GLASS_HAIRLINE = 'linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)';

// Скелетон загрузки Swap — точная копия текущей раскладки (шапка с логотипом +
// пилюля баланса, стеклянная карта, поля, курс, Confirm), чтобы при приходе данных
// ничего не «прыгало». Аква-волна бежит каскадом сверху вниз, блоки плавно
// проявляются, карта мягко «дышит» подсветкой. Стили самодостаточны: скелетон
// рендерится ДО основного <style> экрана, поэтому keyframes объявлены прямо тут.
function SwapSkeleton() {
  // Плашка-заглушка: стекло + бегущий блик. d — задержка (каскад волны сверху вниз).
  const Sk = ({ w, h, r = 10, d = 0, strong = false, style }) => (
    <div
      className={strong ? 'sk sk-strong' : 'sk'}
      style={{ width: w, height: h, borderRadius: r, animationDelay: `${d}s, ${d}s`, ...style }}
    />
  );

  // Поле «You pay / You get» — повторяет реальное поле 1:1 (отступы, радиусы, чип).
  const field = (d) => (
    <div
      className="sk-in"
      style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 16, border: '1px solid rgba(255,255,255,0.10)', padding: '9px 14px', animationDelay: `${d}s` }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Sk w={52} h={12} r={6} d={d} />
        <Sk w={88} h={32} r={12} d={d + 0.03} style={{ border: '1px solid rgba(255,255,255,0.12)' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 8, gap: 10 }}>
        <Sk w="44%" h={30} r={9} d={d + 0.06} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 7 }}>
          <Sk w={62} h={11} r={6} d={d + 0.08} />
          <Sk w={80} h={11} r={6} d={d + 0.1} />
        </div>
      </div>
    </div>
  );

  return (
    <div aria-busy="true" aria-label="Loading">
      <style>{`
        @keyframes skWave { 0% { background-position: 160% 0; } 100% { background-position: -60% 0; } }
        @keyframes skIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        @keyframes skBreath {
          0%, 100% { box-shadow: 0 22px 50px rgba(0,0,0,0.5), 0 0 0 0 rgba(96,189,255,0); border-color: rgba(255,255,255,0.12); }
          50%      { box-shadow: 0 22px 50px rgba(0,0,0,0.5), 0 0 30px 0 rgba(96,189,255,0.12); border-color: rgba(96,189,255,0.22); }
        }
        @keyframes skDot { 0%, 100% { opacity: .35; transform: scale(.8); } 50% { opacity: 1; transform: scale(1); } }
        .sk {
          background-color: rgba(255,255,255,0.065);
          background-image: linear-gradient(100deg, rgba(255,255,255,0) 30%, rgba(96,189,255,0.17) 48%, rgba(255,255,255,0.10) 52%, rgba(255,255,255,0) 70%);
          background-size: 250% 100%;
          background-repeat: no-repeat;
          animation: skWave 1.9s cubic-bezier(0.45,0,0.25,1) infinite, skIn 0.55s cubic-bezier(0.22,1,0.36,1) both;
        }
        .sk-strong { background-color: rgba(255,255,255,0.14); }
        .sk-in { animation: skIn 0.55s cubic-bezier(0.22,1,0.36,1) both; }
        @media (prefers-reduced-motion: reduce) {
          .sk, .sk-in, .sk-card, .sk-dot { animation: none !important; }
        }
      `}</style>

      {/* Шапка: реальный логотип (он уже в кеше после сплэша) + пилюля баланса */}
      <div style={{ marginBottom: 16, marginTop: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <img
            src="/access/logo.png"
            alt=""
            className="sk-in"
            style={{ width: 120, height: 60, borderRadius: 8 }}
            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
          />
          <Sk
            w={132}
            h={42}
            r={14}
            d={0.05}
            style={{ backgroundColor: 'rgba(96,189,255,0.10)', border: '1px solid rgba(96,189,255,0.24)' }}
          />
        </div>
      </div>

      {/* Карта Swap */}
      <div
        className="sk-card"
        style={{
          background: 'rgba(255,255,255,0.055)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          padding: 16,
          marginTop: 40,
          marginBottom: 18,
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 22,
          animation: 'skIn 0.55s cubic-bezier(0.22,1,0.36,1) 0.08s both, skBreath 3.4s ease-in-out 0.7s infinite',
        }}
      >
        {/* Заголовок: «Swap» + индикатор подключения */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <Sk w={66} h={20} r={7} d={0.1} style={{ marginLeft: 4 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 2 }}>
            <span className="sk-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: '#60BDFF', boxShadow: '0 0 8px rgba(96,189,255,0.8)', animation: 'skDot 1.3s ease-in-out infinite' }} />
            <Sk w={30} h={12} r={6} d={0.12} />
          </div>
        </div>

        {field(0.16)}

        {/* Кнопка разворота (обёртка центрирует — transform у .sk занят анимацией) */}
        <div style={{ position: 'relative', height: 16 }}>
          <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 2 }}>
            <Sk w={36} h={36} r="50%" d={0.24} style={{ backgroundColor: 'rgba(38,44,54,0.92)', border: '1px solid rgba(255,255,255,0.16)' }} />
          </div>
        </div>

        {field(0.3)}

        {/* Блок курса */}
        <div
          className="sk-in"
          style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 16, padding: '12px 14px', marginTop: 12, border: '1px solid rgba(255,255,255,0.10)', animationDelay: '0.42s' }}
        >
          <Sk w="58%" h={14} r={7} d={0.42} />
          <Sk w="40%" h={11} r={6} d={0.48} style={{ marginTop: 9 }} />
        </div>
      </div>

      {/* Confirm */}
      <Sk w="100%" h={54} r={16} d={0.56} strong style={{ marginTop: 4, marginBottom: 28 }} />
    </div>
  );
}

function SwapBlock({ wsRate, wsBalance, onDepositClick, onWithdrawClick }) {
  const { t, lang, setLang } = useLang();
  const {
    user: tgUser, userId: tgUserId, username: tgUsername, displayName: tgName, initial: tgInitial,
    avatarUrl: tgAvatar, avatarReady, avatarFailed,
    hideBalance, setHideBalance, hideSmall, setHideSmall,
    timezone, setTimezone, effectiveTz,
    notifDeposit, setNotifDeposit, notifWithdraw, setNotifWithdraw,
  } = useSettings();
  const [pageLoaded, setPageLoaded] = useState(false);
  const [tokens, setTokens] = useState(null);
  const [tradingPairs, setTradingPairs] = useState({});
  const [balance, setBalance] = useState(null);
  const [isBalanceExpanded, setIsBalanceExpanded] = useState(false);

  // Текущий вид: 'swap' | 'deposit' | 'withdraw'
  const [currentView, setCurrentView] = useState('swap');
  
  // Deposit states
  const [depositMode, setDepositMode] = useState('direct'); // 'direct' | 'onchain'
  const [depositToken, setDepositToken] = useState('USDT');
  const [depositDropdownOpen, setDepositDropdownOpen] = useState(false);
  const [depositSearchQuery, setDepositSearchQuery] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [depositNetwork, setDepositNetwork] = useState('TRC20'); // по умолчанию TRC20
  const [depositNetworkOpen, setDepositNetworkOpen] = useState(false);
  const [depositNetworkSearch, setDepositNetworkSearch] = useState('');
  const [depositAddress, setDepositAddress] = useState('');
  const [depositAddrLoading, setDepositAddrLoading] = useState(false);
  const [depositAddrError, setDepositAddrError] = useState('');
  const [depositProcessing, setDepositProcessing] = useState(false);
  const [depositSuccess, setDepositSuccess] = useState('');
  const [depositError, setDepositError] = useState('');
  const [copied, setCopied] = useState(false);

  // Withdraw states
  const [withdrawMode, setWithdrawMode] = useState('direct'); // 'direct' | 'onchain'
  const [withdrawToken, setWithdrawToken] = useState('');
  const [withdrawDropdownOpen, setWithdrawDropdownOpen] = useState(false);
  const [withdrawSearchQuery, setWithdrawSearchQuery] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawNetwork, setWithdrawNetwork] = useState('');
  const [withdrawNetworkOpen, setWithdrawNetworkOpen] = useState(false);
  const [withdrawNetworkSearch, setWithdrawNetworkSearch] = useState('');
  const [activeDD, setActiveDD] = useState(null); // одно открытое выпадающее меню: 'dTok'|'dNet'|'wTok'|'wNet'|'wEx'
  const toggleDD = (id) => setActiveDD((p) => (p === id ? null : id));
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [withdrawExchangeId, setWithdrawExchangeId] = useState('');
  const [withdrawExDropdownOpen, setWithdrawExDropdownOpen] = useState(false);
  const [withdrawExSearchQuery, setWithdrawExSearchQuery] = useState('');
  const [withdrawError, setWithdrawError] = useState('');
  const [withdrawSuccess, setWithdrawSuccess] = useState('');
  const [withdrawProcessing, setWithdrawProcessing] = useState(false);

  // Список бирж
  const listExchange = {
    '1': 'MEXC',
    '2': 'ByBit',
    '3': 'KuCoin',
    '4': 'BitGet',
    '5': 'Huobi',
    '6': 'BingX',
    '7': 'OKX',
    '8': 'BitMart',
    '9': 'LBank',
    '10': 'CoinW',
    '11': 'BitForex',
    '12': 'BitFinex',
    '13': 'XT',
    '14': 'DigitalFinex',
    '15': 'ProBit',
    '16': 'Phemex',
    '17': 'Tapbit',
    '18': 'AscendEX',
    '19': 'Poloniex',
    '20': 'Coinbase',
    '21': 'Kraken'
  };

  const selectIconExchange = (exchangeId) =>
    listExchange[exchangeId]
      ? `/access/indexExchange/${exchangeId}.svg`
      : '/access/indexExchange/0.svg';

  const [searchQuery1, setSearchQuery1] = useState('');
  const [searchQuery2, setSearchQuery2] = useState('');

  const [token1, setToken1] = useState('USDT');
  const [token2, setToken2] = useState('BTC');

  const [amount1, setAmount1] = useState('');
  const [amount2, setAmount2] = useState('');
  const [activeField, setActiveField] = useState('field1');

  const [showDropdown1, setShowDropdown1] = useState(false);
  const [showDropdown2, setShowDropdown2] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState('pending'); // 'pending' | 'success' | 'error'
  const [transactionError, setTransactionError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [swapAnimating, setSwapAnimating] = useState(false);
  const [hasClickedConfirm, setHasClickedConfirm] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);

  // Wallet: история операций и bottom-sheet модалки
  const [history, setHistory] = useState(null);     // null = ещё не грузили
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null); // объект токена для деталки
  const [selectedTx, setSelectedTx] = useState(null); // транзакция для модалки деталей истории
  const [showAllHistory, setShowAllHistory] = useState(false); // лист всей истории
  const [historyLimit, setHistoryLimit] = useState(7); // сколько операций показывать (+7 по кнопке)
  // hideBalance теперь в общем store настроек (useSettings выше)
  const [balanceTab, setBalanceTab] = useState('total'); // 'total' | 'spot' | 'trade'
  const [manageOpen, setManageOpen] = useState(false); // модалка управления отображением токенов
  const [langOpen, setLangOpen] = useState(false); // аккордеон выбора языка в Настройках
  const [notifOpen, setNotifOpen] = useState(false); // аккордеон центра уведомлений
  const [tzOpen, setTzOpen] = useState(false); // аккордеон выбора часового пояса
  const [hideOpen, setHideOpen] = useState(false); // аккордеон «Hide» (баланс/мелкие)
  const [tzSearch, setTzSearch] = useState(''); // поиск часового пояса
  const [idCopied, setIdCopied] = useState(false); // фидбек копирования ID
  // extraTokens — токены БЕЗ баланса, которые юзер вручную включил для показа (с нулём).
  // По умолчанию пусто: видны только токены с балансом (+ USDT). Токены с балансом выключить нельзя.
  const [extraTokens, setExtraTokens] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('extraTokens') || '[]')); } catch { return new Set(); }
  });

  // Спред свопа (проскальзывание пула) — диапазон % из публичного конфига (правится в /config бота)
  const [spreadCfg, setSpreadCfg] = useState({ min: 0.5, max: 1.5 });
  useEffect(() => {
    let alive = true;
    const load = () => fetch(`${URL_API}/public_config`)
      .then((r) => r.json())
      .then((d) => {
        if (alive && d && typeof d.swap_spread_min_pct === 'number') {
          setSpreadCfg({ min: Number(d.swap_spread_min_pct) || 0, max: Number(d.swap_spread_max_pct) || 0 });
        }
      })
      .catch(() => {});
    load();
    // Админ может поменять спред в /config, пока приложение открыто — подтягиваем без перезахода.
    const iv = setInterval(load, 30000);
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive = false; clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  // Transfer (Spot <-> Trade)
  const [tradeBalances, setTradeBalances] = useState({}); // {ASSET: amount} — trade-баланс
  const [tradeSymbol, setTradeSymbol] = useState('BTC'); // активная пара на графике Trade
  const [tradePrice, setTradePrice] = useState(null);    // live-цена с графика (для PnL панели)
  const [tradePrices, setTradePrices] = useState({}); // {ASSET: цена} — курс ТРЕЙДА (независим от swap)
  const [transferDir, setTransferDir] = useState('to_trade'); // 'to_trade' | 'to_spot'
  const [transferToken, setTransferToken] = useState('USDT');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferProcessing, setTransferProcessing] = useState(false);
  const [transferError, setTransferError] = useState('');
  const [transferSuccess, setTransferSuccess] = useState('');

  // const [timer, setTimer] = useState(9);

  // Только НАСТОЯЩИЙ Telegram id — никакого фейкового '1'. Telegram-webview
  // иногда отдаёт id с задержкой: стартуем с того, что есть (или пусто), и
  // дорезолвим ниже. Пока id пустой — запросы с ним просто не уходят (есть guard'ы).
  const [user_id, setUserId] = useState(() => getTgUserId() || '');
  useEffect(() => {
    if (getTgUserId()) return; // id уже есть — дорезолв не нужен
    let n = 0;
    const iv = setInterval(() => {
      n += 1;
      const real = getTgUserId();
      if (real) { setUserId(real); clearInterval(iv); }
      else if (n >= 18) clearInterval(iv); // ~7с — дальше это реально не Telegram
    }, 400);
    return () => clearInterval(iv);
  }, []);

  const TG_INIT_DATA = getTgInitData() || '';

  // Загрузка истории транзакций (свопы/депозиты/выводы)
  const fetchHistory = React.useCallback(async () => {
    if (!user_id) return;
    setHistoryLoading(true);
    try {
      const res = await fetch(`${URL_API}/history?user_id=${encodeURIComponent(user_id)}`);
      const data = await res.json();
      setHistory(Array.isArray(data?.history) ? data.history : []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [user_id]);

  // Загрузка trade-баланса и курса трейда (spot приходит по WS, swap-курс — из tokens.json)
  const fetchWallet = React.useCallback(async () => {
    if (!user_id) return;
    try {
      const res = await fetch(`${URL_API}/wallet?user_id=${encodeURIComponent(user_id)}`);
      const data = await res.json();
      setTradeBalances(data && typeof data.trade === 'object' && data.trade ? data.trade : {});
      setTradePrices(data && typeof data.trade_prices === 'object' && data.trade_prices ? data.trade_prices : {});
    } catch { /* ignore */ }
  }, [user_id]);

  // Trade-баланс нужен всегда (Total в шапке = spot + trade), подтягиваем при готовности user_id
  useEffect(() => { fetchWallet(); }, [fetchWallet]);

  // Подтягиваем историю и балансы при входе в Wallet / Transfer
  useEffect(() => {
    if (currentView === 'portfolio') { fetchHistory(); fetchWallet(); }
    if (currentView === 'transfer') fetchWallet();
  }, [currentView, fetchHistory, fetchWallet]);

  // Реальный адрес пополнения (TRC20/BEP20) из бэкенда — модуль PAYLOAD.
  useEffect(() => {
    if (currentView !== 'deposit' || !depositNetwork || !user_id) return;
    let cancelled = false;
    setDepositAddrLoading(true);
    setDepositAddrError('');
    setDepositAddress('');
    fetch(`${URL_API}/deposit/address?user_id=${encodeURIComponent(user_id)}&network=${encodeURIComponent(depositNetwork)}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (data && data.ok && data.address) setDepositAddress(data.address);
        else setDepositAddrError((data && data.message) || 'Не удалось получить адрес');
      })
      .catch(() => { if (!cancelled) setDepositAddrError('Сеть недоступна'); })
      .finally(() => { if (!cancelled) setDepositAddrLoading(false); });
    return () => { cancelled = true; };
  }, [currentView, depositNetwork, user_id]);

  // Telegram BackButton: «назад» внутри приложения (а не закрытие приложения)
  useEffect(() => {
    const wa = window.Telegram && window.Telegram.WebApp;
    const bb = wa && wa.BackButton;
    if (!bb) return;
    const parent = { deposit: 'portfolio', withdraw: 'portfolio', transfer: 'portfolio', portfolio: 'swap', trade: 'swap', settings: 'swap' };
    const back = () => { const p = parent[currentView]; if (p) setCurrentView(p); };
    try {
      if (currentView && currentView !== 'swap') { bb.onClick(back); bb.show(); }
      else { bb.hide(); }
    } catch (e) {}
    return () => { try { bb.offClick(back); } catch (e) {} };
  }, [currentView]);

  // Отправка перевода Spot <-> Trade
  const submitTransfer = async () => {
    const amt = parseFloat(transferAmount);
    if (!transferToken || !amt || amt <= 0) { setTransferError('Enter amount'); return; }
    setTransferProcessing(true); setTransferError(''); setTransferSuccess('');
    try {
      const res = await fetch(`${URL_API}/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: parseInt(user_id, 10) || 0, asset: transferToken, amount: amt, direction: transferDir }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.status === 'success') {
        // Атомарно обновляем ОБА баланса из ответа (spot и trade), чтобы Total не мерцал
        // (раньше trade обновлялся сразу, а spot догонял по WS → 1450→1550→1450).
        if (data.spot && typeof data.spot === 'object') {
          setBalance((prev) => ({ ...(prev || {}), balances: data.spot }));
        }
        setTradeBalances(data.trade || {});
        setTransferSuccess('Transfer completed');
        setTransferAmount('');
        try { wsBalance && wsBalance.send('get'); } catch {}
      } else {
        setTransferError(data?.detail?.message || data?.message || 'Transfer failed');
      }
    } catch (e) {
      setTransferError('Network error');
    } finally {
      setTransferProcessing(false);
    }
  };

  // Закрытие выпадающих меню (Deposit/Withdraw) кликом вне карточки
  useEffect(() => {
    if (!activeDD) return;
    const onDown = (e) => { if (!e.target.closest('[data-dd]')) setActiveDD(null); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [activeDD]);

  // Технические псевдо-активы (внешние/служебные) — для классификации операций
  const TECH_ASSETS = ['THIS_EXTERNAL', 'THIS_EXTERNAL_ONCHAIN', 'THIS_INTERNAL', 'FEE', 'XFER', '-'];
  const isTechAsset = (a) => TECH_ASSETS.includes(String(a || '').toUpperCase());

  // Метаданные операции для красивого отображения в истории
  const txMeta = (tx) => {
    const fw = String(tx?.from_who || '').toLowerCase();
    let meta;
    if (tx?.kind === 'transfer' || fw === 'transfer') {
      const toTrade = tx?.direction === 'to_trade';
      meta = { kind: 'transfer', label: 'Transfer', sign: '', sub: toTrade ? 'Spot → Trade' : 'Trade → Spot', asset: tx?.asset || tx?.recipient, value: (tx?.amount != null ? tx.amount : tx?.recipient_value), color: '#60BDFF', Icon: FiRepeat };
    } else if (fw.includes('deposit') || (isTechAsset(tx?.give) && !isTechAsset(tx?.recipient))) {
      meta = { kind: 'deposit', label: 'Deposit', sign: '+', asset: tx?.recipient, value: tx?.recipient_value, color: '#3FD18B', Icon: FiArrowDownLeft };
    } else if (fw.includes('withdraw') || (!isTechAsset(tx?.give) && isTechAsset(tx?.recipient))) {
      meta = { kind: 'withdraw', label: 'Withdraw', sign: '−', asset: tx?.give, value: tx?.give_value, color: '#FF6B6B', Icon: FiArrowUpRight };
    } else {
      meta = { kind: 'swap', label: 'Swap', sign: '+', asset: tx?.recipient, sub: `${tx?.give} → ${tx?.recipient}`, value: tx?.recipient_value, color: '#60BDFF', Icon: FiRefreshCw };
    }
    // Статус ожидания (неподтверждённый on-chain депозит) — жёлто-оранжевый + часы
    const st = String(tx?.status || '').toLowerCase();
    meta.pending = (st === 'awaiting');
    if (meta.pending) { meta.color = '#F5A623'; meta.Icon = FiClock; }
    meta.statusLabel = meta.pending ? 'Pending' : (st === 'completed' || st === 'done' ? 'Completed' : (st ? st.charAt(0).toUpperCase() + st.slice(1) : 'Completed'));
    return meta;
  };

  const formatTxDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const loc = lang === 'ru' ? 'ru-RU' : 'en-US';
      const tzOpt = effectiveTz ? { timeZone: effectiveTz } : {};
      return d.toLocaleDateString(loc, { month: 'short', day: 'numeric', ...tzOpt }) + ' · ' +
        d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit', ...tzOpt });
    } catch { return ''; }
  };

  // История по конкретному токену
  const tokenHistory = (sym) => {
    if (!Array.isArray(history) || !sym) return [];
    const S = String(sym).toUpperCase();
    return history.filter((tx) => String(tx?.give).toUpperCase() === S || String(tx?.recipient).toUpperCase() === S);
  };

  // Открыть деталку токена
  const openTokenSheet = (token) => setSelectedToken(token);
  const closeTokenSheet = () => setSelectedToken(null);

  // Множество "залоченных" токенов: USDT + всё, где есть баланс (spot или trade) — их выключить нельзя.
  const lockedTokenSet = () => {
    const s = new Set(['USDT']);
    for (const [k, v] of Object.entries(balance?.balances || {})) if ((v || 0) > 0) s.add(String(k).toUpperCase());
    for (const [k, v] of Object.entries(tradeBalances || {})) if ((v || 0) > 0) s.add(String(k).toUpperCase());
    return s;
  };

  // Включить/выключить показ токена БЕЗ баланса. Залоченные (USDT/с балансом) не трогаем.
  const toggleTokenExtra = (sym) => {
    const S = String(sym || '').toUpperCase();
    if (lockedTokenSet().has(S)) return;
    setExtraTokens((prev) => {
      const next = new Set(prev);
      if (next.has(S)) next.delete(S); else next.add(S);
      try { localStorage.setItem('extraTokens', JSON.stringify([...next])); } catch (e) {}
      return next;
    });
  };

  // Ссылка на обозреватель по сети + сокращение хэша/адреса
  const explorerTxUrl = (network, hash) => {
    if (!hash) return null;
    const n = String(network || '').toUpperCase();
    if (n === 'BEP20') return `https://bscscan.com/tx/${hash}`;
    if (n === 'TRC20') return `https://tronscan.org/#/transaction/${hash}`;
    return null;
  };
  const shortHash = (h) => (h && h.length > 16 ? `${h.slice(0, 10)}…${h.slice(-8)}` : (h || ''));

  // Из деталки токена — перейти к действию с предвыбранным токеном
  const tokenAction = (action, sym) => {
    setSelectedToken(null);
    if (action === 'deposit') { setDepositToken(sym); setTimeout(() => setCurrentView('deposit'), 60); }
    else if (action === 'withdraw') { setWithdrawToken(sym); setTimeout(() => setCurrentView('withdraw'), 60); }
    else if (action === 'transfer') { setTransferToken(sym); setTransferError(''); setTransferSuccess(''); setTimeout(() => setCurrentView('transfer'), 60); }
  };

  // Subscribe to shared WS channels
  useEffect(() => {
    if (!wsBalance) return;
    const unsub = wsBalance.addListener((payload) => {
      try {
        if (payload && payload.balances && Object.keys(payload.balances).length === 0) {
          setBalance({ balances: { USDT: 0 }, user_id: payload.user_id || '123' });
        } else {
          setBalance(payload);
        }
      } catch (e) {
        console.error('Balance payload error:', e);
      }
    });
    // ask for fresh data
    wsBalance.send('get');
    return () => { unsub && unsub(); };
  }, [wsBalance]);

  useEffect(() => {
    if (!wsRate) return;
    const unsub = wsRate.addListener((data) => {
      try {
        setTokens(data);
        if (data && data['trading-pairs']) setTradingPairs(data['trading-pairs']);
      } catch (e) {
        console.error('Rate payload error:', e);
      }
    });
    // ask for fresh data
    wsRate.send('get');
    return () => { unsub && unsub(); };
  }, [wsRate]);

  // Анимация входа - убираем задержку
  useEffect(() => {
    setPageLoaded(true);
  }, []);

  // Рефы для отслеживания кликов вне дропдаунов
  const dropdown1Ref = useRef(null);
  const dropdown2Ref = useRef(null);
  const lastToastPairRef = useRef(null);

  // Ref for balance card
  const balanceCardRef = useRef(null);

  // Общий обработчик клика вне
  useEffect(() => {
    const handleClickOutside = e => {
      if (showDropdown1 && dropdown1Ref.current && !dropdown1Ref.current.contains(e.target)) {
        setShowDropdown1(false);
      }
      if (showDropdown2 && dropdown2Ref.current && !dropdown2Ref.current.contains(e.target)) {
        setShowDropdown2(false);
      }
      // Close balance card when clicking outside
      if (isBalanceExpanded && balanceCardRef.current && !balanceCardRef.current.contains(e.target)) {
        setIsBalanceExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown1, showDropdown2, isBalanceExpanded]);

  // Check pair when tokens change and show toast if not supported
  useEffect(() => {
    if (!tokens || !token1 || !token2) return;
    
    const isSame = token1 === token2;
    const ruleFor1 = tradingPairs[token1];
    const ruleFor2 = tradingPairs[token2];
    const pairAllowed = !ruleFor1 && !ruleFor2
      ? true
      : (ruleFor1 === token2) || (ruleFor2 === token1);

    const pairKey = `${token1}-${token2}`;
    
    // Only show toast if pair is not allowed AND it's a different pair than last time
    if (!isSame && !pairAllowed && lastToastPairRef.current !== pairKey) {
      lastToastPairRef.current = pairKey;
      setToastMessage('This pair is not supported');
      setShowToast(true);
    } else if (pairAllowed || isSame) {
      // Reset ref when pair becomes valid
      lastToastPairRef.current = null;
    }
  }, [token1, token2, tokens, tradingPairs]);

  // Auto-hide toast: 3 sec for short text, 5 sec for long text
  useEffect(() => {
    if (!showToast || !toastMessage) return;
    const duration = toastMessage.length > 50 ? 5000 : 3000;
    const timer = setTimeout(() => {
      setShowToast(false);
      setTimeout(() => setToastMessage(''), 300);
    }, duration);
    return () => clearTimeout(timer);
  }, [showToast, toastMessage]);

  if (!tokens) return <SwapSkeleton />;

  // Total balance (USD)
  const totalBalanceUsd = (() => {
    if (!balance?.balances || !tokens) return 0;
    let total = 0;
    Object.keys(balance.balances || {}).forEach((currency) => {
      const amount = balance.balances[currency] || 0;
      const price = tokens?.[currency]?.sell?.price ?? 0;
      total += amount * price;
    });
    return total;
  })();

  // Trade-баланс в USD — по КУРСУ ТРЕЙДА (независим от swap), фолбэк на swap-курс
  const tradeBalanceUsd = (() => {
    if (!tradeBalances) return 0;
    let total = 0;
    Object.keys(tradeBalances || {}).forEach((currency) => {
      const price = tradePrices?.[currency] ?? tokens?.[currency]?.sell?.price ?? 0;
      total += (tradeBalances[currency] || 0) * price;
    });
    return total;
  })();

  // Top 5 tokens by USD value
  const topTokens = (() => {
    if (!balance?.balances || !tokens) return [];
    return Object.keys(balance.balances || {})
      .map((currency) => {
        const amount = balance.balances[currency] || 0;
        const price = tokens?.[currency]?.sell?.price ?? 0;
        const usdValue = amount * price;
        return {
          currency,
          amount,
          usdValue,
          icon: iconPath(currency),
          title: tokens?.[currency]?.title || currency,
        };
      })
      .filter((t) => (t.amount || 0) > 0)
      .filter((t) => t.usdValue >= 1 || String(t.currency || '').toUpperCase() === 'USDT')
      .sort((a, b) => b.usdValue - a.usdValue)
      .slice(0, 5);
  })();

  const closeExpanded = () => setIsBalanceExpanded(false);

  // Копирование в буфер
  const handleCopy = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const tmp = document.createElement('textarea');
        tmp.value = text;
        document.body.appendChild(tmp);
        tmp.select();
        document.execCommand('copy');
        document.body.removeChild(tmp);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Copy failed:', e);
    }
  };

  const handleDepositClick = () => {
    closeExpanded();
    // Переключаемся на вид Deposit вместо открытия модалки
    setTimeout(() => {
      setCurrentView('deposit');
    }, 140);
  };
  const handleWithdrawClick = () => {
    closeExpanded();
    // Переключаемся на вид Withdraw вместо открытия модалки
    setTimeout(() => {
      setCurrentView('withdraw');
    }, 140);
  };
  const handlePortfolioClick = () => {
    closeExpanded();
    setTimeout(() => {
      setCurrentView('portfolio');
    }, 140);
  };
  // Навигация из меню "Total balance": Swap / Trade / Wallet
  const handleSwapNavClick = () => {
    closeExpanded();
    setTimeout(() => {
      setCurrentView('swap');
    }, 140);
  };
  const handleTradeClick = () => {
    closeExpanded();
    setTimeout(() => {
      setCurrentView('trade');
    }, 140);
  };
  // Перевод между балансами (Spot ↔ Trade) — переход на отдельный экран
  const handleTransferClick = () => {
    closeExpanded();
    setTransferError(''); setTransferSuccess('');
    setTimeout(() => setCurrentView('transfer'), 140);
  };

  // Sanitize numeric fields for withdraw/deposit amount with limits
  const sanitizeAmount = (val) => {
    let v = String(val ?? '').replace(/,/g, '.');
    v = v.replace(/[^0-9.]/g, '');
    const parts = v.split('.');
    if (parts.length > 2) {
      v = parts[0] + '.' + parts.slice(1).join('');
    }
    // Limit to 12 characters total (allows for 8-9 digits + decimal + decimals)
    if (v.length > 12) {
      v = v.substring(0, 12);
    }
    return v;
  };

  // Проверка валидности формы вывода (только Direct)
  const amountOk = withdrawAmount && !isNaN(parseFloat(withdrawAmount)) && Number(parseFloat(withdrawAmount)) > 0;
  const wAddrTrim = (withdrawAddress || '').trim();
  const wAddrValid = wAddrTrim.length >= 16 && wAddrTrim.length <= 120 && /^[A-Za-z0-9:_-]+$/.test(wAddrTrim);
  const isWithdrawFormValid = Boolean(withdrawToken && amountOk && withdrawNetwork.trim() && wAddrValid);

  // Обработка вывода
  const handleWithdrawSubmit = async () => {
    setWithdrawError('');
    setWithdrawSuccess('');
    setWithdrawProcessing(true);

    const amt = parseFloat(withdrawAmount);
    const available = Number(balance?.balances?.[withdrawToken] || 0);
    
    if (!isFinite(amt) || amt <= 0) {
      setWithdrawError('Please enter a valid amount');
      setWithdrawProcessing(false);
      return;
    }
    if (amt > available) {
      setWithdrawError('Insufficient funds');
      setWithdrawProcessing(false);
      return;
    }

    try {
      const response = await fetch(`${URL_API}/withdraw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'initData': TG_INIT_DATA },
        body: JSON.stringify({
          type: 'withdraw',
          user_id: user_id,
          token: withdrawToken,
          amount: withdrawAmount,
          ...(withdrawMode === 'direct' ? { network: withdrawNetwork } : {}),
          address: withdrawAddress,
          mode: withdrawMode,
          ...(withdrawMode === 'onchain' && withdrawExchangeId
            ? { exchange_id: withdrawExchangeId, exchange: listExchange[withdrawExchangeId] }
            : {})
        }),
      });

      const data = await response.json().catch(() => ({}));
      
      if (!response.ok) {
        const detail = data?.detail || data || {};
        // Handle structured error response
        let errorMsg = 'Withdrawal error';
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (typeof detail === 'object') {
          errorMsg = detail.message || detail.error || 'Withdrawal error';
          
          // Handle specific error types
          if (detail.error === 'authentication_expired' || detail.error === 'authentication_error' || detail.error === 'authentication_missing') {
            errorMsg = detail.message || 'Your session has expired. Please refresh the page and try again.';
          } else if (detail.error === 'insufficient_balance') {
            errorMsg = detail.message || `Insufficient balance. Available: ${detail.balance || 0}, Required: ${detail.required || 0}`;
          } else if (detail.error === 'invalid_onchain_address') {
            errorMsg = detail.message || 'Invalid onchain address format.';
          }
        }
        throw new Error(errorMsg);
      }

      // Обновляем баланс
      try { wsBalance && wsBalance.send('get'); } catch {}

      setWithdrawSuccess('Withdrawal request submitted.');
      // Очистка полей
      setWithdrawToken('');
      setWithdrawAmount('');
      setWithdrawNetwork('');
      setWithdrawAddress('');
      setWithdrawExchangeId('');
      setWithdrawProcessing(false);
    } catch (err) {
      setWithdrawError(err?.message || 'Withdrawal error');
      setWithdrawProcessing(false);
    }
  };

  const t1 = tokens?.[token1];
  const t2 = tokens?.[token2];

  // Плавная Swap-анимация
  const handleSwap = () => {
    setSwapAnimating(true);
    setTimeout(() => {
      const tmp1 = token1, tmp2 = token2;
      setToken1(tmp2);
      setToken2(tmp1);
      const a1 = amount1, a2 = amount2;
      setAmount1(a2);
      setAmount2(a1);
      setActiveField('field1');
      setTimeout(() => setSwapAnimating(false), 400);
    }, 200);
  };

  // Sanitize and limit input amounts
  const sanitizeSwapAmount = (value) => {
    let v = String(value || '').replace(/,/g, '.');
    // Keep only digits and dots
    v = v.replace(/[^0-9.]/g, '');
    // Collapse multiple dots
    const parts = v.split('.');
    if (parts.length > 2) {
      v = parts[0] + '.' + parts.slice(1).join('');
    }
    // Limit to 15 characters to prevent overflow
    if (v.length > 15) {
      v = v.substring(0, 15);
    }
    return v;
  };

  const onChangeAmount1 = e => {
    setActiveField('field1');
    setAmount1(sanitizeSwapAmount(e.target.value));
  };
  const onChangeAmount2 = e => {
    setActiveField('field2');
    setAmount2(sanitizeSwapAmount(e.target.value));
  };

  // Вычисления конвертации
  const val1 = parseFloat(amount1) || 0;
  const val2 = parseFloat(amount2) || 0;
  const sellPrice1 = t1?.sell?.price ?? 0;
  const buyPrice2 = t2?.buy?.price ?? 0;
  // Пер-монетный спред (пулы ликвидности не идентичны): детерминированный по символу
  // процент в [min, max], одинаковый для монеты, но разный между монетами. Уменьшает
  // получаемую сумму (проскальзывание). Стабилен — не «дёргается» при обновлении курса.
  const _spreadPct = (sym) => {
    const s = String(sym || '').toUpperCase();
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = (h * 16777619) >>> 0; }
    const lo = Math.max(0, spreadCfg.min || 0);
    const hi = Math.max(lo, spreadCfg.max || 0);
    return lo + ((h % 10000) / 10000) * (hi - lo);
  };
  const _spreadCoin = token1 === 'USDT' ? token2 : token1;
  // Если НА ВЫХОДЕ USDT — спред нулевой всегда: отдаём полную долларовую
  // стоимость входа (напр. отдал SUI на $3560.77 -> получил 3560.77 USDT).
  // Спред остаётся только при покупке монеты за USDT.
  const spreadFactor = token2 === 'USDT' ? 1 : (1 - _spreadPct(_spreadCoin) / 100);
  const computedAmount2 = activeField === 'field1'
    ? (buyPrice2 ? (val1 * sellPrice1 / buyPrice2 * spreadFactor).toFixed(8) : '')
    : amount2;
  const computedAmount1 = activeField === 'field2'
    ? (sellPrice1 ? (val2 * buyPrice2 / sellPrice1 / spreadFactor).toFixed(8) : '')
    : amount1;
  const displayAmount1 = activeField === 'field2' ? computedAmount1 : amount1;
  let displayAmount2 = activeField === 'field1' ? computedAmount2 : amount2;
  if (parseFloat(displayAmount2) > 1) {
    displayAmount2 = parseFloat(displayAmount2).toFixed(5);
  }

  // Проверка пары и блокировка Confirm
  const isSameToken = token1 === token2;
  const ruleFor1 = tradingPairs[token1];
  const ruleFor2 = tradingPairs[token2];
  const isPairAllowed = !ruleFor1 && !ruleFor2
    ? true
    : (ruleFor1 === token2) || (ruleFor2 === token1);

  const usdValue = (parseFloat(displayAmount1) || 0) * sellPrice1;
  // Button is always active - validation happens in modal
  const isConfirmDisabled = false;

  // Fee
  const token2Amount = parseFloat(displayAmount2) || 0;
  const t2SellPrice = t2?.sell?.price ?? 0;
  const token2Usd = token2Amount * t2SellPrice;
  let feeUsd = 0;
  if (token2Usd <= 100) feeUsd = 1;
  else if (token2Usd <= 1000) feeUsd = 2;
  else if (token2Usd < 10000) feeUsd = 3;
  else feeUsd = 5;
  const feeToken2 = t2SellPrice > 0 ? feeUsd / t2SellPrice : 0;
  const finalReceived = (token2Amount - feeToken2).toFixed(9);

  // Фильтрация/сортировка
  // You pay — ТОЛЬКО токены, которые у меня есть (баланс > 0) + поиск
  const filteredTokens1 = Object.entries(tokens || {})
    .filter(([key, info]) =>
      key !== 'trading-pairs' &&
      key !== token2 &&
      (balance?.balances?.[key] || 0) > 0 &&
      (!searchQuery1 ||
        (info?.title || '').toLowerCase().includes(searchQuery1.toLowerCase()) ||
        key.toLowerCase().includes(searchQuery1.toLowerCase()))
    );
  // You get — все токены (мои в приоритете сверху) + поиск
  const filteredTokens2 = Object.entries(tokens || {})
    .filter(([key, info]) =>
      key !== 'trading-pairs' &&
      key !== token1 &&
      (!searchQuery2 ||
        (info?.title || '').toLowerCase().includes(searchQuery2.toLowerCase()) ||
        key.toLowerCase().includes(searchQuery2.toLowerCase()))
    );
  // Сортировка по USD-стоимости баланса (по убыванию)
  const sortByBalanceUsd = (a, b) => {
    const va = (balance?.balances?.[a[0]] || 0) * (tokens?.[a[0]]?.sell?.price ?? 0);
    const vb = (balance?.balances?.[b[0]] || 0) * (tokens?.[b[0]]?.sell?.price ?? 0);
    return vb - va;
  };
  const sortedTokens1 = [...filteredTokens1].sort(sortByBalanceUsd);
  const sortedTokens2 = [...filteredTokens2].sort(sortByBalanceUsd);

  // Обработка Confirm
  const handleConfirm = async () => {
    setTransactionStatus('pending');
    setTransactionError('');
    setIsProcessing(true);
    
    const payload = {
      user_id,
      give: token1,
      recipient: token2,
      give_value: parseFloat(displayAmount1) || 0,
      recipient_value: parseFloat(finalReceived) || 0,
      from_who: 'swap'
    };

    try {
      const response = await fetch(`${URL_API}/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'initData': TG_INIT_DATA },
        body: JSON.stringify(payload)
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonErr) {
        console.error('[Swap] JSON parse error:', jsonErr);
        setTransactionStatus('error');
        setTransactionError('Server error: unable to process response');
        setIsProcessing(false);
        return;
      }

      if (!response.ok) {
        console.log('[Swap] API error response:', data);
        const detail = data?.detail || data || {};
        let errorMsg = 'Transaction failed';
        
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (typeof detail === 'object') {
          errorMsg = detail.message || detail.error || 'Transaction failed';
          
          // Handle specific error types
          if (detail.error === 'authentication_expired' || detail.error === 'authentication_error' || detail.error === 'authentication_missing') {
            errorMsg = detail.message || 'Your session has expired. Please refresh the page and try again.';
          } else if (detail.error === 'insufficient_balance') {
            errorMsg = 'Insufficient funds';
          } else if (detail.balance !== undefined && detail.required !== undefined) {
            // Simplified error message
          }
        }
        
        console.log('[Swap] Setting error state:', errorMsg);
        setIsProcessing(false);
        setTransactionError(errorMsg);
        setTransactionStatus('error');
      } else {
        console.log('[Swap] Success:', data);
        setTransactionStatus('success');
        setIsProcessing(false);
        // Request current balance via WS
        try { wsBalance && wsBalance.send('get'); } catch {}
      }
    } catch (e) {
      console.error('[Swap] Network error:', e);
      setTransactionStatus('error');
      setTransactionError('Network error: ' + e.message);
      setIsProcessing(false);
    }
  };

  // Переиспользуемые стили для форм Deposit/Withdraw
  const dfField = { background: 'rgba(255,255,255,0.03)', border: 'none', borderRadius: 14, padding: '12px 14px', marginBottom: 12 };
  const dfLabel = { color: '#7E8AA0', fontSize: 11.5, fontWeight: 600, letterSpacing: '0.3px', marginBottom: 8, display: 'block' };
  const dfInput = { width: '100%', background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontSize: 15 };
  const dfDropdown = { position: 'absolute', top: '100%', left: 0, marginTop: 6, ...GLASS_PANEL, borderRadius: 16, zIndex: 30, width: 260, maxHeight: 300, overflowY: 'auto', padding: 6 };
  const dfSearch = { width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, outline: 'none', color: '#fff', padding: '10px 12px', fontSize: 14, boxSizing: 'border-box', marginBottom: 4 };
  const dfOpt = { padding: '10px 10px', borderRadius: 12, cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
  const dfPill = { background: 'rgba(96,189,255,0.12)', display: 'flex', alignItems: 'center', gap: 7, padding: '7px 12px', borderRadius: 12, cursor: 'pointer', border: '1px solid rgba(96,189,255,0.18)' };
  const pasteInto = async (setter) => { try { const t = await navigator.clipboard.readText(); if (t) setter(t.trim()); } catch (e) {} };

  return (
    <div
      className="animated-container"
      style={{
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @keyframes sheetUp {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
          @keyframes sheetFade {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes rowIn {
            from { transform: translateY(8px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          @keyframes walletIn {
            from { transform: translateY(14px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          /* Минималистичные кнопки Wallet с анимациями */
          .wallet-action {
            position: relative;
            overflow: hidden;
            animation: walletIn 0.45s cubic-bezier(0.22,1,0.36,1) both;
            transition: transform 0.22s cubic-bezier(0.22,1,0.36,1), box-shadow 0.25s ease, border-color 0.22s ease, background-color 0.22s ease;
          }
          .wallet-action:hover {
            transform: translateY(-4px);
            border-color: var(--acc);
            background-color: var(--acc-bg);
            box-shadow: 0 10px 24px rgba(0,0,0,0.32);
          }
          .wallet-action:active {
            transform: translateY(-1px) scale(0.955);
            transition-duration: 0.08s;
          }
          .wallet-action .wa-ic-wrap {
            transition: background-color 0.22s ease, transform 0.25s cubic-bezier(0.22,1,0.36,1);
          }
          .wallet-action:hover .wa-ic-wrap {
            background-color: var(--acc-bg2) !important;
            transform: scale(1.06);
          }
          .wallet-action .wa-ic {
            transition: transform 0.28s cubic-bezier(0.34,1.56,0.64,1);
          }
          .wallet-action:hover .wa-ic {
            transform: translateY(-2px) scale(1.14);
          }
          /* лёгкий «блик» при наведении */
          .wallet-action::after {
            content: '';
            position: absolute;
            top: 0; left: -120%;
            width: 60%; height: 100%;
            background: linear-gradient(100deg, transparent, rgba(255,255,255,0.07), transparent);
            transition: left 0.5s ease;
            pointer-events: none;
          }
          .wallet-action:hover::after { left: 130%; }
          /* Поля Deposit/Withdraw */
          .df-field { transition: border-color 0.2s ease, background-color 0.2s ease; }
          .df-field:focus-within { border-color: rgba(96,189,255,0.55); background-color: rgba(96,189,255,0.05); }
          .df-input::placeholder { color: #5C6270; }
          .df-opt { transition: background-color 0.15s ease; }
          .df-opt:hover { background-color: rgba(96,189,255,0.08); }
          .df-opt:active { background-color: rgba(96,189,255,0.13); }
          .df-pill { transition: transform 0.15s ease, background-color 0.2s ease, border-color 0.2s ease; }
          .df-pill:hover { background-color: rgba(96,189,255,0.18); }
          .df-pill:active { transform: scale(0.96); }
          .df-dd { animation: ddIn 0.24s cubic-bezier(0.22,1,0.36,1) both; transform-origin: top center; }
          @keyframes ddIn { from { opacity: 0; transform: translateY(-6px) scale(0.97); } to { opacity: 1; transform: none; } }
          .bal049 { animation: bal049pulse 3.6s ease-in-out infinite; transition: filter 0.2s ease, transform 0.15s ease; }
          .bal049:hover { filter: brightness(1.12); }
          .bal049:active { transform: scale(0.985); }
          @keyframes bal049pulse { 0%,100% { box-shadow: 0 0 0 1px rgba(255,255,255,0.04), 0 6px 16px rgba(0,0,0,0.5); } 50% { box-shadow: 0 0 0 1px rgba(255,255,255,0.10), 0 6px 16px rgba(0,0,0,0.5); } }
          .df-send { transition: transform 0.15s ease, filter 0.2s ease, box-shadow 0.25s ease; }
          .df-send:not(:disabled):hover { filter: brightness(0.96); transform: translateY(-1px); }
          .df-send:not(:disabled):active { transform: scale(0.98); }
          .df-copy { transition: transform 0.15s ease, filter 0.2s ease; }
          .df-copy:hover { filter: brightness(1.06); }
          .df-copy:active { transform: scale(0.98); }
          @keyframes slideIn {
            from { transform: translateY(20px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          @keyframes viewIn {
            0%   { opacity: 0; transform: translateY(10px) scale(0.99); filter: brightness(0.45); }
            55%  { opacity: 1; }
            100% { opacity: 1; transform: translateY(0) scale(1); filter: brightness(1); }
          }
          @keyframes fadeIn { 
            from { opacity: 0; } 
            to { opacity: 1; } 
          }
          @keyframes modalSlideIn {
            from { transform: translateY(30px) scale(0.95); opacity: 0; }
            to { transform: translateY(0) scale(1); opacity: 1; }
          }
          @keyframes iconScale {
            0% { transform: scale(0); }
            50% { transform: scale(1.1); }
            100% { transform: scale(1); }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }
          @keyframes circleDraw {
            to { strokeDashoffset: 0; }
          }
          @keyframes checkDraw {
            to { strokeDashoffset: 0; }
          }
          @keyframes checkmarkDraw {
            from { opacity: 0; transform: scale(0.8); }
            to { opacity: 1; transform: scale(1); }
          }
          @keyframes arrowGentle {
            0%, 100% { 
              transform: translateY(0);
              opacity: 1;
              filter: drop-shadow(0 0 0px rgba(96, 189, 255, 0));
            }
            50% { 
              transform: translateY(-3px);
              opacity: 1;
              filter: drop-shadow(0 0 3px rgba(96, 189, 255, 0.6));
            }
          }
          @keyframes toastFadeIn {
            from {
              opacity: 0;
              transform: translate(-50%, -50%) scale(0.9);
            }
            to {
              opacity: 1;
              transform: translate(-50%, -50%) scale(1);
            }
          }
          @keyframes toastFadeOut {
            from {
              opacity: 1;
              transform: translate(-50%, -50%) scale(1);
            }
            to {
              opacity: 0;
              transform: translate(-50%, -50%) scale(0.9);
            }
          }
          @keyframes tabUnderline {
            from {
              width: 0;
              opacity: 0;
            }
            to {
              width: 100%;
              opacity: 1;
            }
          }
          @keyframes smoothSwap {
            0% { transform: translateY(0) scale(1); opacity: 1; }
            50% { transform: translateY(-10px) scale(0.95); opacity: 0.7; }
            100% { transform: translateY(0) scale(1); opacity: 1; }
          }
          @keyframes rotateSwap {
            0% { transform: translate(-50%, -50%) rotate(0deg) scale(1); }
            50% { transform: translate(-50%, -50%) rotate(180deg) scale(1.1); }
            100% { transform: translate(-50%, -50%) rotate(360deg) scale(1); }
          }

          .animated-container { 
            animation: slideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1); 
          }
          .swap-button {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .swap-button:active { 
            transform: scale(0.96); 
          }
          .token-selector-swapping {
            animation: smoothSwap 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .swap-icon-rotating {
            animation: rotateSwap 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .fade-in { 
            animation: fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1); 
          }

          .dropdown {
            opacity: 0;
            transform: translateY(-10px);
            transition: opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1), 
                        transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .dropdown.show {
            opacity: 1;
            transform: translateY(0);
          }
          .number-transition {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .confirm-button {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .confirm-button:active {
            transform: scale(0.98);
          }
          .bal-row { transition: background-color .15s ease, transform .12s ease; }
          .bal-row:hover { background-color: rgba(255,255,255,0.06); }
          .bal-row:active { transform: scale(0.985); }
          .modal-ghost { transition: all .2s cubic-bezier(0.4,0,0.2,1); }
          .modal-ghost:hover { background-color: rgba(255,255,255,0.06); }
          .modal-ghost:active { transform: scale(0.985); }
          .confirm-button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        `}
      </style>

      {/* Header (только в Swap): logo + Total balance */}
      <div style={{ marginBottom: 16, marginTop: 10 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          <img
            src="/access/logo.png"
            alt="Logo"
            onClick={() => setCurrentView('swap')}
            style={{ 
              width: 120, 
              height: 60, 
              borderRadius: 8,
              cursor: 'pointer',
              transition: 'opacity 0.2s ease',
            }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.8'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          />

          <div
            ref={balanceCardRef}
            style={{
              flex: 1,
              minWidth: 0,
              maxWidth: '47%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              zIndex: 50,
            }}
          >
            <button
              onClick={() => setIsBalanceExpanded((v) => !v)}
              aria-expanded={isBalanceExpanded}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                padding: '7px 12px',
                borderRadius: 14,
                background: 'linear-gradient(135deg, rgba(96,189,255,0.20) 0%, rgba(255,255,255,0.08) 48%, rgba(8,145,178,0.18) 100%)',
                border: '1px solid rgba(96,189,255,0.28)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                outline: 'none',
                cursor: 'pointer',
                alignSelf: 'flex-end',
                maxWidth: '100%',
                minWidth: 0,
              }}
            >
              <span style={{
                fontSize: 18,
                fontWeight: 700,
                color: '#FFFFFF',
                letterSpacing: '-0.02em',
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
                transform: 'scaleX(0.94)',
                transformOrigin: 'left center',
                display: 'inline-block',
              }}>
                $ {(totalBalanceUsd + tradeBalanceUsd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/,/g, ' ')}
              </span>

              <svg
                width="13"
                height="13"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  flexShrink: 0,
                  transform: isBalanceExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.25s ease',
                }}
              >
                <path d="M3.5 6 8 10.5 12.5 6" stroke="#FFFFFF" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* Dropdown overlay */}
            <div
              aria-hidden={!isBalanceExpanded}
              style={{
                position: 'absolute',
                right: 0,
                top: 'calc(100% + 12px)',
                width: 'calc(100% + 70px)',
                maxHeight: isBalanceExpanded ? 720 : 0,
                opacity: isBalanceExpanded ? 1 : 0,
                transform: isBalanceExpanded ? 'translateY(0px)' : 'translateY(-6px)',
                overflow: 'hidden',
                transition: 'max-height 0.28s ease, opacity 0.22s ease, transform 0.28s ease',
                pointerEvents: isBalanceExpanded ? 'auto' : 'none',
                background: 'radial-gradient(120% 75% at 82% 0%, rgba(96,189,255,0.12), transparent 58%), linear-gradient(180deg, rgba(32,34,42,0.93) 0%, rgba(18,20,26,0.95) 100%)',
                backdropFilter: 'blur(26px)',
                WebkitBackdropFilter: 'blur(26px)',
                boxShadow: '0 18px 44px rgba(0,0,0,0.45)',
                border: '1px solid rgba(96,189,255,0.10)',
                boxSizing: 'border-box',
                borderRadius: 22,
                padding: isBalanceExpanded ? 8 : 0,
                display: 'flex',
                flexDirection: 'column',
                zIndex: 100,
              }}
            >
              {/* Навигация — чистые строки без обводок */}
              {[
                { label: t('nav.swap'), Icon: FiRefreshCw, onClick: handleSwapNavClick },
                { label: t('nav.trade'), Icon: FiTrendingUp, onClick: handleTradeClick },
                { label: t('nav.wallet'), Icon: FiCreditCard, onClick: handlePortfolioClick },
              ].map(({ label, Icon, onClick }, i, arr) => (
                <React.Fragment key={label}>
                  <button
                    className="bal-row"
                    onClick={onClick}
                    style={{
                      width: '100%', background: 'transparent', border: 'none', borderRadius: 14,
                      padding: '10px 10px', color: '#FFFFFF', fontSize: 15, fontWeight: 600,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
                    }}
                  >
                    <span style={{ width: 34, height: 34, borderRadius: 11, background: 'rgba(96,189,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon size={18} color="#60BDFF" />
                    </span>
                    <span style={{ flex: 1, textAlign: 'left' }}>{label}</span>
                    <FiChevronRight size={16} color="#5A5F6B" />
                  </button>
                  {i < arr.length - 1 && (
                    <hr style={{ border: 'none', height: 1, margin: '1px 8px', background: 'linear-gradient(90deg, transparent, rgba(96,189,255,0.22), transparent)' }} />
                  )}
                </React.Fragment>
              ))}

              {/* Разделитель секции (другой стиль) */}
              <hr style={{ border: 'none', height: 1, margin: '8px 6px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.14), transparent)' }} />

              {/* Top 5 tokens (fallback USDT 0.00) */}
              {(() => {
                const list = (topTokens && topTokens.length > 0)
                  ? topTokens.slice(0, 5)
                  : [{ currency: 'USDT', usdValue: 0, icon: iconPath('usdt'), title: 'USDT' }];

                return (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {list.map((token, idx) => {
                      const usdValue = Number(token?.usdValue || 0);
                      const usdText = usdValue
                        .toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        .replace(/,/g, ' ');
                      const rawBalance = balance?.balances?.[token.currency] || 0;
                      const tokenAmount = formatNumber(rawBalance);

                      return (
                        <React.Fragment key={token.currency}>
                          <div
                            className="bal-row"
                            onClick={() => { setIsBalanceExpanded(false); openTokenSheet({ currency: token.currency, icon: token.icon }); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 13, cursor: 'pointer', padding: '8px 10px', borderRadius: 12 }}
                          >
                            <img
                              src={token.icon}
                              alt={token.currency}
                              style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0 }}
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                              <div style={{ color: 'white', fontWeight: 600, fontSize: 18, lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                <span>{tokenAmount}</span>
                                <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 600, opacity: 0.85 }}>{token.currency}</span>
                              </div>
                              <div style={{ color: '#7E8190', fontSize: 13, fontWeight: 500, lineHeight: 1.1, marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                ≈ {usdText} USD
                              </div>
                            </div>
                          </div>
                          {idx < list.length - 1 && (
                            <hr style={{ border: 'none', height: 1, margin: '0 12px', background: 'rgba(255,255,255,0.05)' }} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Разделитель перед настройками */}
              <hr style={{ border: 'none', height: 1, margin: '8px 6px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.14), transparent)' }} />

              {/* Настройки — чистая строка, без обводки */}
              <button
                className="bal-row"
                onClick={() => { setCurrentView('settings'); setIsBalanceExpanded(false); }}
                style={{
                  width: '100%', background: 'transparent', border: 'none', borderRadius: 14,
                  padding: '10px 10px', color: '#FFFFFF', fontSize: 15, fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
                }}
              >
                <span style={{ width: 34, height: 34, borderRadius: 11, background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FiSettings size={18} color="#A8AEBC" />
                </span>
                <span style={{ flex: 1, textAlign: 'left' }}>{t('settings.open')}</span>
                <FiChevronRight size={16} color="#5A5F6B" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* SETTINGS VIEW (полноэкранно, без Total balance) */}
      {currentView === 'settings' && (
        <div style={{ minHeight: 380, animation: 'viewIn 0.42s cubic-bezier(0.22,1,0.36,1) both' }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 44, marginBottom: 18 }}>
            <button aria-label="Back" onClick={() => setCurrentView('swap')} style={{ position: 'absolute', left: 0, background: 'none', border: 'none', color: '#9AA0AE', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 6, transition: 'color 0.2s ease' }} onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }} onMouseLeave={(e) => { e.currentTarget.style.color = '#9AA0AE'; }}>
              <FiArrowLeft size={22} />
            </button>
            <span style={{ color: '#fff', fontSize: 19, fontWeight: 800, letterSpacing: '0.5px' }}>Apex</span>
          </div>

          {/* ACCOUNT */}
          <div style={{ color: '#6E7480', fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', margin: '4px 4px 8px' }}>{t('settings.account')}</div>
          <div style={{ background: 'rgba(22,22,27,0.55)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', border: '1px solid rgba(34,34,40,0.7)', borderRadius: 16, marginBottom: 22, padding: '15px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#23262F', color: '#C7CDD8', fontSize: 21, fontWeight: 600, position: 'relative', border: '1px solid #2C303A' }}>
              <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{tgInitial}</span>
              {tgAvatar && !avatarFailed && (
                <img src={tgAvatar} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: avatarReady ? 1 : 0, transition: 'opacity 0.35s ease' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#fff', fontSize: 16, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tgName}</div>
              {tgUsername && <div style={{ color: '#7A8090', fontSize: 12.5, marginTop: 2 }}>{tgUsername}</div>}
            </div>
            {tgUserId && (
              <button onClick={() => { handleCopy(tgUserId); setIdCopied(true); setTimeout(() => setIdCopied(false), 1400); }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.04)', border: '1px solid #2A2A33', borderRadius: 9, padding: '6px 10px', color: idCopied ? '#3FD18B' : '#9AA0AE', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
                {idCopied ? <FiCheck size={13} /> : <FiCopy size={12} />} {idCopied ? t('common.copied') : `ID ${tgUserId}`}
              </button>
            )}
          </div>

          {/* WALLET-секция скрыта по просьбе (Управление токенами + Скрытие) */}
          {false && (<>
          <div style={{ color: '#6E7480', fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', margin: '4px 4px 8px' }}>{t('settings.wallet')}</div>
          <div style={{ background: 'rgba(22,22,27,0.55)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', border: '1px solid rgba(34,34,40,0.7)', borderRadius: 16, marginBottom: 22, overflow: 'hidden' }}>
            <button onClick={() => setManageOpen(true)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 13, padding: '13px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><FiGrid size={17} color="#A8AEBC" /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{t('settings.manageTokens')}</div>
                <div style={{ color: '#727885', fontSize: 11.5 }}>{t('settings.manageTokensDesc')}</div>
              </div>
              <FiChevronRight size={18} color="#565C68" />
            </button>
            <div style={{ borderTop: '1px solid #1F1F26' }}>
              <button onClick={() => setHideOpen(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 13, padding: '13px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><FiEyeOff size={17} color="#A8AEBC" /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{t('settings.hide')}</div>
                  <div style={{ color: '#727885', fontSize: 11.5 }}>{t('settings.hideDesc')}</div>
                </div>
                <FiChevronDown size={18} color="#565C68" style={{ transform: hideOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.25s ease' }} />
              </button>
              <div style={{ maxHeight: hideOpen ? 130 : 0, overflow: 'hidden', transition: 'max-height 0.28s ease' }}>
                <div style={{ padding: '4px 14px 14px 14px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {[
                    { on: !!hideBalance, set: () => setHideBalance(v => !v), title: t('settings.hideBalance') },
                    { on: !!hideSmall, set: () => setHideSmall(v => !v), title: t('settings.hideSmall') },
                  ].map((r) => (
                    <div key={r.title} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ color: '#C7CDD8', fontSize: 13.5, fontWeight: 500 }}>{r.title}</span>
                      <button onClick={r.set} aria-label="toggle" style={{ width: 44, height: 26, borderRadius: 13, border: 'none', position: 'relative', cursor: 'pointer', background: r.on ? '#3FD18B' : '#33333D', transition: 'background 0.25s ease', flexShrink: 0 }}>
                        <span style={{ position: 'absolute', top: 3, left: r.on ? 21 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.25s cubic-bezier(0.22,1,0.36,1)' }} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          </>)}

          {/* GENERAL — Язык + Регион + Уведомления (аккордеоны) */}
          <div style={{ color: '#6E7480', fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', margin: '4px 4px 8px' }}>{t('settings.general')}</div>
          <div style={{ background: 'rgba(22,22,27,0.55)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', border: '1px solid rgba(34,34,40,0.7)', borderRadius: 16, marginBottom: 22, overflow: 'hidden' }}>
            {/* Язык */}
            <div>
              <button onClick={() => setLangOpen(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 13, padding: '13px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><FiGlobe size={17} color="#A8AEBC" /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{t('settings.language')}</div>
                </div>
                <span style={{ color: '#8A90A0', fontSize: 13, fontWeight: 600, marginRight: 4 }}>{(LANGS.find(l => l.code === lang) || LANGS[0]).flag} {(LANGS.find(l => l.code === lang) || LANGS[0]).label}</span>
                <FiChevronDown size={18} color="#565C68" style={{ transform: langOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.25s ease' }} />
              </button>
              <div style={{ maxHeight: langOpen ? 130 : 0, overflow: 'hidden', transition: 'max-height 0.28s ease' }}>
                <div style={{ padding: '0 13px 12px 13px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {LANGS.map((l) => { const on = lang === l.code; return (
                    <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px', borderRadius: 10, cursor: 'pointer', border: `1px solid ${on ? '#3A4452' : '#26262E'}`, background: on ? 'rgba(255,255,255,0.04)' : 'transparent', color: on ? '#fff' : '#9AA0AE', fontWeight: 600, fontSize: 13.5 }}>
                      <span style={{ fontSize: 16 }}>{l.flag}</span><span style={{ flex: 1, textAlign: 'left' }}>{l.label}</span>{on && <FiCheck size={15} color="#3FD18B" />}
                    </button>
                  ); })}
                </div>
              </div>
            </div>
            {/* Регион */}
            <div style={{ borderTop: '1px solid #1F1F26' }}>
              <button onClick={() => setTzOpen(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 13, padding: '13px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><FiMapPin size={17} color="#A8AEBC" /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{t('settings.timezone')}</div>
                </div>
                <span style={{ color: '#8A90A0', fontSize: 12.5, fontWeight: 600, marginRight: 4, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(() => { const z = TIMEZONES.find(z => z.tz === timezone) || TIMEZONES[0]; return z.tz === 'auto' ? `${z.label} (${t('settings.recommended')})` : z.label; })()}</span>
                <FiChevronDown size={18} color="#565C68" style={{ transform: tzOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.25s ease' }} />
              </button>
              <div style={{ maxHeight: tzOpen ? 360 : 0, overflow: 'hidden', transition: 'max-height 0.3s ease' }}>
                <div style={{ padding: '0 13px 12px 13px' }}>
                  <div style={{ position: 'relative', marginBottom: 8 }}>
                    <FiSearch size={14} color="#6A6F7C" style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }} />
                    <input type="text" value={tzSearch} onChange={(e) => setTzSearch(e.target.value)} placeholder={lang === 'ru' ? 'Поиск…' : 'Search…'}
                      style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.03)', border: '1px solid #2A2A33', borderRadius: 10, outline: 'none', color: '#fff', padding: '9px 10px 9px 32px', fontSize: 13 }} />
                  </div>
                  <div style={{ maxHeight: 250, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {TIMEZONES.filter((z) => !tzSearch || z.label.toLowerCase().includes(tzSearch.toLowerCase()) || z.tz.toLowerCase().includes(tzSearch.toLowerCase())).map((z) => { const on = timezone === z.tz; return (
                      <button key={z.tz} onClick={() => { setTimezone(z.tz); setTzOpen(false); setTzSearch(''); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px', borderRadius: 10, cursor: 'pointer', border: `1px solid ${on ? '#3A4452' : '#26262E'}`, background: on ? 'rgba(255,255,255,0.04)' : 'transparent', color: on ? '#fff' : '#9AA0AE', fontWeight: 600, fontSize: 13 }}>
                        <span style={{ flex: 1, textAlign: 'left' }}>{z.tz === 'auto' ? `${z.label} (${t('settings.recommended')})` : z.label}</span>{on && <FiCheck size={15} color="#3FD18B" />}
                      </button>
                    ); })}
                  </div>
                </div>
              </div>
            </div>
            {/* Уведомления */}
            <div style={{ borderTop: '1px solid #1F1F26' }}>
              <button onClick={() => setNotifOpen(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 13, padding: '13px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><FiBell size={17} color="#A8AEBC" /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{t('settings.notifications')}</div>
                </div>
                <FiChevronDown size={18} color="#565C68" style={{ transform: notifOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.25s ease' }} />
              </button>
              <div style={{ maxHeight: notifOpen ? 130 : 0, overflow: 'hidden', transition: 'max-height 0.28s ease' }}>
                <div style={{ padding: '4px 14px 14px 14px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {[
                    { on: !!notifDeposit, set: () => setNotifDeposit(v => !v), title: t('settings.notifDeposit') },
                    { on: !!notifWithdraw, set: () => setNotifWithdraw(v => !v), title: t('settings.notifWithdraw') },
                  ].map((r) => (
                    <div key={r.title} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ color: '#C7CDD8', fontSize: 13.5, fontWeight: 500 }}>{r.title}</span>
                      <button onClick={r.set} aria-label="toggle" style={{ width: 44, height: 26, borderRadius: 13, border: 'none', position: 'relative', cursor: 'pointer', background: r.on ? '#3FD18B' : '#33333D', transition: 'background 0.25s ease', flexShrink: 0 }}>
                        <span style={{ position: 'absolute', top: 3, left: r.on ? 21 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.25s cubic-bezier(0.22,1,0.36,1)' }} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* SUPPORT — связь с поддержкой в Telegram */}
          <div style={{ color: '#6E7480', fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', margin: '4px 4px 8px' }}>{t('settings.supportTitle')}</div>
          <div style={{ background: 'rgba(22,22,27,0.55)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', border: '1px solid rgba(34,34,40,0.7)', borderRadius: 16, marginBottom: 22, overflow: 'hidden' }}>
            <button
              onClick={() => { const u = 'https://t.me/apex_support143'; try { if (window?.Telegram?.WebApp?.openTelegramLink) window.Telegram.WebApp.openTelegramLink(u); else window.open(u, '_blank'); } catch { window.open(u, '_blank'); } }}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 13, padding: '13px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
            >
              <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg, rgba(96,189,255,0.22) 0%, rgba(8,145,178,0.22) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FiSend size={16} color="#60BDFF" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{t('settings.support')}</div>
                <div style={{ color: '#727885', fontSize: 11.5 }}>{t('settings.supportDesc')}</div>
              </div>
              <FiChevronRight size={18} color="#565C68" />
            </button>
          </div>
        </div>
      )}

      {/* SWAP VIEW */}
      {currentView === 'swap' && (
        <div style={{ animation: 'viewIn 0.42s cubic-bezier(0.22,1,0.36,1) both' }}>
          <div style={{ background: 'rgba(255,255,255,0.055)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', boxShadow: '0 22px 50px rgba(0,0,0,0.5)', padding: 16, marginTop: 40, marginBottom: 18, border: '1px solid rgba(255,255,255,0.12)', borderRadius: 22 }}>
            {/* Заголовок */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ marginLeft: 4 }}>
                <h3 style={{ color: 'white', margin: 0, fontSize: 19, fontWeight: 700 }}>Swap</h3>
              </div>
              <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, marginRight: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#FF6063', animation: 'blink 1.5s ease-in-out infinite' }} />
                Live
              </span>
            </div>

            {/* You pay */}
            <div style={{ position: 'relative' }} ref={dropdown1Ref}>
              <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 16, border: '1px solid rgba(255,255,255,0.10)', padding: '9px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: 'rgba(255,255,255,0.70)', fontSize: 12, fontWeight: 600 }}>You pay</span>
                  <div onClick={() => { setShowDropdown1((p) => !p); setSearchQuery1(''); }} style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', gap: 7, padding: '6px 11px', borderRadius: 12, cursor: 'pointer' }}>
                    <img src={iconPath(token1)} style={{ width: 20, height: 20, borderRadius: '50%' }} alt={token1} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    <span style={{ color: '#EAF6FF', fontWeight: 700, fontSize: 14 }}>{token1}</span>
                    <FiChevronRight size={14} color="#FFFFFF" style={{ transform: showDropdown1 ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s ease' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 8, gap: 10 }}>
                  <input style={{ width: '58%', height: 34, backgroundColor: 'transparent', border: 'none', outline: 'none', color: 'white', fontSize: 26, fontWeight: 600 }} placeholder="0" value={displayAmount1} onChange={onChangeAmount1} onFocus={() => setActiveField('field1')} inputMode="decimal" />
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: 'rgba(255,255,255,0.60)', fontSize: 13 }}>{t1?.sell?.price ? `~$${((t1.sell.price) * (parseFloat(displayAmount1) || 0)).toFixed(2)}` : '~$0.00'}</div>
                    {token1 && balance?.balances?.[token1] !== undefined && (
                      <div style={{ fontSize: 12, marginTop: 3 }}>
                        <span style={{ color: 'rgba(255,255,255,0.60)' }}>Avail: </span>
                        <span onClick={() => { const a = balance?.balances?.[token1] || 0; if (a > 0) { setAmount1(String(a)); setActiveField('field1'); } }} style={{ color: '#38BDF8', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}>{formatNumber(balance?.balances?.[token1] || 0)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {showDropdown1 && (
                <div className="df-dd" style={{ position: 'absolute', top: 48, right: 0, ...GLASS_PANEL, borderRadius: 18, zIndex: 30, width: 256, overflow: 'hidden', padding: 6 }}>
                  <div style={{ position: 'relative', padding: '2px 2px 8px', marginBottom: 4, backgroundImage: GLASS_HAIRLINE, backgroundSize: '100% 1px', backgroundPosition: 'bottom', backgroundRepeat: 'no-repeat' }}>
                    <FiSearch size={14} color="#8FA3B8" style={{ position: 'absolute', left: 14, top: 'calc(50% - 3px)', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                    <input type="text" placeholder="Search" value={searchQuery1} onChange={(e) => setSearchQuery1(e.target.value)} autoFocus style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, outline: 'none', color: '#fff', padding: '9px 12px 9px 34px', fontSize: 14 }} />
                  </div>
                  <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                    {sortedTokens1.map(([key, info]) => (
                      <div key={key} className="df-opt" style={{ padding: '9px 10px', borderRadius: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'background-color 0.15s ease' }} onClick={() => { setToken1(key); setShowDropdown1(false); setSearchQuery1(''); }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <img src={iconPath(key)} style={{ width: 30, height: 30, borderRadius: '50%' }} alt={info?.title || key} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                          <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{key}</span>
                        </div>
                        <span style={{ fontSize: 12.5, fontWeight: 600, color: (balance?.balances?.[key] || 0) > 0 ? '#60BDFF' : '#5C6270' }}>{formatNumber(balance?.balances?.[key] || 0)}</span>
                      </div>
                    ))}
                    {sortedTokens1.length === 0 && (<div style={{ padding: '18px 14px', textAlign: 'center', color: '#6A6A78', fontSize: 13 }}>No tokens found</div>)}
                  </div>
                </div>
              )}
            </div>

            {/* Кнопка разворота */}
            <div style={{ position: 'relative', height: 16 }}>
              <div onClick={handleSwap} className={`swap-button ${swapAnimating ? 'swap-icon-rotating' : ''}`} style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 5, display: 'flex', justifyContent: 'center', cursor: 'pointer', transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="18" cy="18" r="18" fill="rgba(255,255,255,0.14)" />
                  <circle cx="18" cy="18" r="17.5" stroke="rgba(255,255,255,0.22)" />
                  <path d="M14.2667 20H10L16.4 26V10H14.2667V20ZM19.6 13V26H21.7333V16H26L19.6 10V13Z" fill="#FFFFFF" />
                </svg>
              </div>
            </div>

            {/* You get */}
            <div style={{ position: 'relative' }} ref={dropdown2Ref}>
              <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 16, border: '1px solid rgba(255,255,255,0.10)', padding: '9px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: 'rgba(255,255,255,0.70)', fontSize: 12, fontWeight: 600 }}>You get</span>
                  <div onClick={() => { setShowDropdown2((p) => !p); setSearchQuery2(''); }} style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', gap: 7, padding: '6px 11px', borderRadius: 12, cursor: 'pointer' }}>
                    <img src={iconPath(token2)} style={{ width: 20, height: 20, borderRadius: '50%' }} alt={token2} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    <span style={{ color: '#EAF6FF', fontWeight: 700, fontSize: 14 }}>{token2}</span>
                    <FiChevronRight size={14} color="#FFFFFF" style={{ transform: showDropdown2 ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s ease' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 8, gap: 10 }}>
                  <input style={{ width: '58%', height: 34, backgroundColor: 'transparent', border: 'none', outline: 'none', color: 'white', fontSize: 26, fontWeight: 600 }} placeholder="0" value={displayAmount2} onChange={onChangeAmount2} onFocus={() => setActiveField('field2')} inputMode="decimal" />
                  <div style={{ color: 'rgba(255,255,255,0.60)', fontSize: 13, textAlign: 'right' }}>{displayAmount2 && t2?.sell?.price ? `$${(t2.sell.price * (parseFloat(displayAmount2) || 0)).toFixed(2)}` : '~$0.00'}</div>
                </div>
              </div>
              {showDropdown2 && (
                <div className="df-dd" style={{ position: 'absolute', top: 48, right: 0, ...GLASS_PANEL, borderRadius: 18, zIndex: 30, width: 256, overflow: 'hidden', padding: 6 }}>
                  <div style={{ position: 'relative', padding: '2px 2px 8px', marginBottom: 4, backgroundImage: GLASS_HAIRLINE, backgroundSize: '100% 1px', backgroundPosition: 'bottom', backgroundRepeat: 'no-repeat' }}>
                    <FiSearch size={14} color="#8FA3B8" style={{ position: 'absolute', left: 14, top: 'calc(50% - 3px)', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                    <input type="text" placeholder="Search" value={searchQuery2} onChange={(e) => setSearchQuery2(e.target.value)} autoFocus style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, outline: 'none', color: '#fff', padding: '9px 12px 9px 34px', fontSize: 14 }} />
                  </div>
                  <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                    {sortedTokens2.map(([key, info]) => (
                      <div key={key} className="df-opt" style={{ padding: '9px 10px', borderRadius: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'background-color 0.15s ease' }} onClick={() => { setToken2(key); setShowDropdown2(false); setSearchQuery2(''); }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <img src={iconPath(key)} style={{ width: 30, height: 30, borderRadius: '50%' }} alt={info?.title || key} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                          <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{key}</span>
                        </div>
                        <span style={{ fontSize: 12.5, fontWeight: 600, color: (balance?.balances?.[key] || 0) > 0 ? '#60BDFF' : '#5C6270' }}>{formatNumber(balance?.balances?.[key] || 0)}</span>
                      </div>
                    ))}
                    {sortedTokens2.length === 0 && (<div style={{ padding: '18px 14px', textAlign: 'center', color: '#6A6A78', fontSize: 13 }}>No tokens found</div>)}
                  </div>
                </div>
              )}
            </div>

            {/* Курс */}
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 16, padding: '12px 14px', marginTop: 12, border: '1px solid rgba(255,255,255,0.10)' }}>
              {t1?.sell?.price && t2?.buy?.price ? (
                <>
                  <div style={{ color: '#EAF6FF', fontSize: 13.5, fontWeight: 600 }}>
                    <b>1</b> {token1} = <b style={{ color: '#fff' }}>{(t1.sell.price / t2.buy.price * spreadFactor).toFixed(8)}</b> {token2}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.60)', fontSize: 12, marginTop: 4 }}>
                    Sell: <b style={{ color: 'rgba(255,255,255,0.85)' }}>{formatNumber(t2.sell?.price)}</b> &nbsp;|&nbsp; Buy: <b style={{ color: 'rgba(255,255,255,0.85)' }}>{formatNumber(t2.buy?.price)}</b>
                  </div>
                </>
              ) : (
                <div style={{ color: '#7E7E95', fontSize: 13 }}>Loading rate…</div>
              )}
            </div>
          </div>

          {/* Confirm (минималистичная) */}
          <button
            className="df-send"
            onClick={() => {
              const isSame = token1 === token2;
              const ruleFor1 = tradingPairs[token1];
              const ruleFor2 = tradingPairs[token2];
              const pairAllowed = !ruleFor1 && !ruleFor2 ? true : (ruleFor1 === token2) || (ruleFor2 === token1);
              const usdVal = (parseFloat(displayAmount1) || 0) * sellPrice1;
              if (isSame || !pairAllowed || usdVal <= 1) {
                setTransactionStatus('error');
                setTransactionError(isSame ? 'Cannot swap same token' : !pairAllowed ? 'Trading pair not supported' : 'Ensure amount is more than 1 dollar and tokens are selected');
                setShowModal(true);
              } else {
                setHasClickedConfirm(true);
                setTransactionStatus('pending');
                setTransactionError('');
                setIsProcessing(false);
                setShowModal(true);
              }
            }}
            style={{ width: '100%', backgroundColor: '#FFFFFF', boxShadow: 'none', marginTop: 4, marginBottom: 28, borderRadius: 16, fontWeight: 700, fontSize: 15, height: 54, border: 'none', color: '#0A0E16', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            <FiCheck size={19} />
            <span>Confirm</span>
          </button>
        </div>
      )}

      {/* DEPOSIT VIEW */}
      {currentView === 'deposit' && (
        <div style={{ minHeight: 380, animation: 'slideIn 0.42s cubic-bezier(0.22,1,0.36,1) both' }}>
          {/* Nav */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 40, marginBottom: 16 }}>
            <button aria-label="Back" onClick={() => setCurrentView('portfolio')} style={{ position: 'absolute', left: 0, background: 'none', border: 'none', color: '#9AA0AE', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 6, transition: 'color 0.2s ease' }} onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }} onMouseLeave={(e) => { e.currentTarget.style.color = '#9AA0AE'; }}>
              <FiArrowLeft size={22} />
            </button>
            <span style={{ color: '#fff', fontSize: 17, fontWeight: 700, letterSpacing: '0.2px' }}>Deposit</span>
          </div>

          {/* QR сверху */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <div style={{ background: '#fff', padding: 12, borderRadius: 16, lineHeight: 0, boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}>
              <QRCodeSVG value={depositAddress || ' '} size={150} bgColor="#ffffff" fgColor="#0E0E12" level="M" />
            </div>
          </div>

          {/* Coin — только USDT */}
          <div className="df-field" data-dd style={{ ...dfField, position: 'relative' }}>
            <span style={dfLabel}>Coin</span>
            <div onClick={() => toggleDD('dTok')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', color: '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <img src={iconPath('USDT')} alt="USDT" style={{ width: 26, height: 26, borderRadius: '50%' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                <span style={{ fontWeight: 600, fontSize: 15 }}>USDT</span>
                <span style={{ color: '#7E8190', fontSize: 13 }}>Tether</span>
              </div>
              <FiChevronRight size={16} color="#60BDFF" style={{ transform: activeDD === 'dTok' ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s ease' }} />
            </div>
            {activeDD === 'dTok' && (
              <div className="df-dd" style={{ ...dfDropdown, left: 0, right: 0, width: 'auto', top: 'calc(100% + 6px)' }}>
                <div className="df-opt" style={dfOpt} onClick={() => { setDepositToken('USDT'); setActiveDD(null); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <img src={iconPath('USDT')} style={{ width: 26, height: 26, borderRadius: '50%' }} alt="USDT" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    <span style={{ fontWeight: 600 }}>USDT</span>
                  </div>
                  <span style={{ color: '#7E8190', fontSize: 12 }}>Tether</span>
                </div>
              </div>
            )}
          </div>

          {/* Network — только TRC20 / BEP20 */}
          <div className="df-field" data-dd style={{ ...dfField, position: 'relative' }}>
            <span style={dfLabel}>Network</span>
            <div onClick={() => toggleDD('dNet')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', color: '#fff' }}>
              {depositNetwork && networkById(depositNetwork) ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <img src={netIcon(networkById(depositNetwork))} alt="" style={{ width: 24, height: 24, borderRadius: '50%' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  <span style={{ fontWeight: 600, fontSize: 15 }}>{networkById(depositNetwork).name}</span>
                </div>
              ) : (<span style={{ color: '#5C6270', fontSize: 15 }}>Select network</span>)}
              <FiChevronRight size={16} color="#60BDFF" style={{ transform: activeDD === 'dNet' ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s ease' }} />
            </div>
            {activeDD === 'dNet' && (
              <div className="df-dd" style={{ ...dfDropdown, left: 0, right: 0, width: 'auto', top: 'calc(100% + 6px)' }}>
                {['TRC20', 'BEP20'].map((nid) => { const n = networkById(nid); if (!n) return null; return (
                  <div key={nid} className="df-opt" style={{ ...dfOpt, justifyContent: 'flex-start', gap: 10 }} onClick={() => { setDepositNetwork(nid); setActiveDD(null); }}>
                    <img src={netIcon(n)} alt="" style={{ width: 26, height: 26, borderRadius: '50%' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    <span style={{ fontWeight: 600 }}>{n.name}</span>
                  </div>
                ); })}
              </div>
            )}
          </div>

          {/* Адрес + мелкая копирка */}
          <div className="df-field" style={dfField}>
            <span style={dfLabel}>Deposit address</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <code style={{ flex: 1, color: depositAddrError ? '#FF7A7A' : '#fff', fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all', lineHeight: 1.5 }}>
                {depositAddrLoading ? 'Получаем адрес…' : (depositAddrError || depositAddress || '—')}
              </code>
              <button className="df-copy" disabled={!depositAddress} onClick={() => depositAddress && handleCopy(depositAddress)} style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 9, background: copied ? 'rgba(63,209,139,0.15)' : 'rgba(96,189,255,0.12)', border: '1px solid ' + (copied ? 'rgba(63,209,139,0.3)' : 'rgba(96,189,255,0.2)'), color: copied ? '#3FD18B' : '#60BDFF', cursor: depositAddress ? 'pointer' : 'not-allowed', opacity: depositAddress ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {copied ? <FiCheck size={16} /> : <FiCopy size={16} />}
              </button>
            </div>
          </div>

          <div style={{ color: '#7E8190', fontSize: 12, textAlign: 'center', lineHeight: 1.5 }}>
            Send only <span style={{ color: '#EAF6FF', fontWeight: 600 }}>USDT</span>
            {depositNetwork && networkById(depositNetwork) ? <> via <span style={{ color: '#EAF6FF', fontWeight: 600 }}>{networkById(depositNetwork).name}</span></> : null} to this address.
          </div>
        </div>
      )}

      {/* WITHDRAW VIEW */}
      {currentView === 'withdraw' && (
        <div style={{ minHeight: 380, animation: 'slideIn 0.42s cubic-bezier(0.22,1,0.36,1) both' }}>
          {/* Nav */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 40, marginBottom: 16 }}>
            <button aria-label="Back" onClick={() => { setCurrentView('portfolio'); setWithdrawError(''); setWithdrawSuccess(''); }} style={{ position: 'absolute', left: 0, background: 'none', border: 'none', color: '#9AA0AE', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 6, transition: 'color 0.2s ease' }} onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }} onMouseLeave={(e) => { e.currentTarget.style.color = '#9AA0AE'; }}>
              <FiArrowLeft size={22} />
            </button>
            <span style={{ color: '#fff', fontSize: 17, fontWeight: 700, letterSpacing: '0.2px' }}>Withdraw</span>
          </div>

          {/* Адрес получателя + Paste + валидация */}
          <div className="df-field" style={{ ...dfField, marginBottom: wAddrTrim.length > 0 && !wAddrValid ? 6 : 12, ...((wAddrTrim.length > 0 && !wAddrValid) ? { border: '1px solid rgba(255,122,122,0.55)' } : {}) }}>
            <span style={dfLabel}>Recipient address</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input className="df-input" type="text" placeholder="Enter wallet address" value={withdrawAddress} onChange={(e) => setWithdrawAddress(e.target.value)} style={dfInput} />
              <button aria-label="Paste" onClick={() => pasteInto(setWithdrawAddress)} style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 9, background: 'rgba(96,189,255,0.12)', border: '1px solid rgba(96,189,255,0.2)', color: '#60BDFF', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiClipboard size={16} />
              </button>
            </div>
          </div>
          {wAddrTrim.length > 0 && !wAddrValid && (
            <div style={{ color: '#FF7A7A', fontSize: 12, marginBottom: 12, paddingLeft: 4 }}>Enter a valid wallet address</div>
          )}

          {/* Монета + сумма (без MAX) */}
          <div className="df-field" style={{ ...dfField, padding: '10px 14px' }}>
            <div data-dd style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, position: 'relative' }}>
              <span style={{ ...dfLabel, marginBottom: 0 }}>You withdraw</span>
              <div className="df-pill" style={dfPill} onClick={() => toggleDD('wTok')}>
                {withdrawToken ? (
                  <>
                    <img src={iconPath(withdrawToken)} alt={withdrawToken} style={{ width: 20, height: 20, borderRadius: '50%' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    <span style={{ color: '#EAF6FF', fontWeight: 600, fontSize: 13 }}>{withdrawToken}</span>
                  </>
                ) : (<span style={{ color: '#60BDFF', fontWeight: 600, fontSize: 13 }}>Select</span>)}
                <FiChevronRight size={14} color="#60BDFF" style={{ transform: activeDD === 'wTok' ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s ease' }} />
              </div>
              {activeDD === 'wTok' && (
                <div className="df-dd" style={{ ...dfDropdown, left: 'auto', right: 0, top: 'calc(100% + 6px)' }}>
                  <div style={{ position: 'relative' }}>
                    <FiSearch size={14} color="#5C6270" style={{ position: 'absolute', left: 14, top: 14 }} />
                    <input className="df-input" type="text" placeholder="Search token" value={withdrawSearchQuery} onChange={(e) => setWithdrawSearchQuery(e.target.value)} style={{ ...dfSearch, paddingLeft: 36 }} />
                  </div>
                  {tokens && Object.entries(tokens)
                    .filter(([key, info]) => { if (key === 'trading-pairs') return false; const hasBalance = balance?.balances?.[key] != null && balance?.balances?.[key] > 0; if (!hasBalance && key !== 'USDT') return false; if (!withdrawSearchQuery) return true; const q = withdrawSearchQuery.toLowerCase(); return (info?.title || '').toLowerCase().includes(q) || key.toLowerCase().includes(q); })
                    .map(([key, info]) => (
                      <div key={key} className="df-opt" style={dfOpt} onClick={() => { setWithdrawToken(key); setWithdrawNetwork(''); setActiveDD(null); setWithdrawSearchQuery(''); }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <img src={iconPath(key)} style={{ width: 30, height: 30, borderRadius: '50%' }} alt={info?.title || key} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                          <span style={{ fontWeight: 600 }}>{key}</span>
                        </div>
                        <span style={{ fontSize: 12, color: '#60BDFF' }}>{formatNumber(balance?.balances?.[key] || 0)}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 10 }}>
              <input className="df-input" type="text" placeholder="0" value={withdrawAmount} onChange={(e) => setWithdrawAmount(sanitizeAmount(e.target.value))} inputMode="decimal" style={{ ...dfInput, fontSize: 24, fontWeight: 600, width: '60%' }} />
              <span style={{ color: '#7E8190', fontSize: 13, whiteSpace: 'nowrap' }}>~${withdrawToken && tokens?.[withdrawToken]?.sell?.price ? ((parseFloat(withdrawAmount) || 0) * (tokens[withdrawToken]?.sell?.price ?? 0)).toFixed(2) : '0.00'}</span>
            </div>
            {withdrawToken && (
              <div style={{ marginTop: 4, fontSize: 12 }}>
                <span style={{ color: '#7E8190' }}>Available: </span>
                <span
                  onClick={() => { const a = balance?.balances?.[withdrawToken] || 0; if (a > 0) setWithdrawAmount(String(a)); }}
                  title="Use full balance"
                  style={{ color: '#60BDFF', fontWeight: 600, cursor: 'pointer', userSelect: 'none', transition: 'opacity 0.2s ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.7'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                >
                  {formatNumber(balance?.balances?.[withdrawToken] || 0)} {withdrawToken}
                </span>
              </div>
            )}
          </div>

          {/* Сеть (под токен) */}
          {withdrawToken && (
            <div className="df-field" data-dd style={{ ...dfField, position: 'relative' }}>
              <span style={dfLabel}>Network</span>
              <div onClick={() => toggleDD('wNet')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', color: '#fff' }}>
                {withdrawNetwork && networkById(withdrawNetwork) ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <img src={netIcon(networkById(withdrawNetwork))} alt="" style={{ width: 24, height: 24, borderRadius: '50%' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    <span style={{ fontWeight: 600, fontSize: 15 }}>{networkById(withdrawNetwork).name}</span>
                  </div>
                ) : (<span style={{ color: '#5C6270', fontSize: 15 }}>Select network for {withdrawToken}</span>)}
                <FiChevronRight size={16} color="#60BDFF" style={{ transform: activeDD === 'wNet' ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s ease' }} />
              </div>
              {activeDD === 'wNet' && (
                <div className="df-dd" style={{ ...dfDropdown, left: 0, right: 0, width: 'auto', top: 'calc(100% + 6px)' }}>
                  {networksForToken(withdrawToken).map((nid) => { const n = networkById(nid); if (!n) return null; return (
                    <div key={nid} className="df-opt" style={{ ...dfOpt, justifyContent: 'flex-start', gap: 10 }} onClick={() => { setWithdrawNetwork(nid); setActiveDD(null); }}>
                      <img src={netIcon(n)} alt="" style={{ width: 26, height: 26, borderRadius: '50%' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      <span style={{ fontWeight: 600 }}>{n.name}</span>
                    </div>
                  ); })}
                </div>
              )}
            </div>
          )}

          {withdrawError && (<div style={{ background: 'rgba(255,122,122,0.1)', border: '1px solid rgba(255,122,122,0.35)', borderRadius: 12, padding: '11px 14px', marginBottom: 12, color: '#FF7A7A', fontSize: 13.5, textAlign: 'center' }}>{withdrawError}</div>)}
          {withdrawSuccess && (<div style={{ background: 'rgba(63,209,139,0.1)', border: '1px solid rgba(63,209,139,0.35)', borderRadius: 12, padding: '11px 14px', marginBottom: 12, color: '#3FD18B', fontSize: 13.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, animation: 'fadeIn 0.3s ease' }}><FiCheck size={18} />{withdrawSuccess}</div>)}

          <button
            className="df-send"
            onClick={handleWithdrawSubmit}
            disabled={!isWithdrawFormValid || withdrawProcessing}
            style={{ width: '100%', backgroundColor: (!isWithdrawFormValid || withdrawProcessing) ? '#2A2A33' : '#FF7A7A', border: 'none', borderRadius: 14, padding: '14px 20px', height: 50, color: (!isWithdrawFormValid || withdrawProcessing) ? '#6A6A78' : '#2A0E0E', fontSize: 15, fontWeight: 700, cursor: (!isWithdrawFormValid || withdrawProcessing) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 4 }}
          >
            <FiArrowUpRight size={18} />
            {withdrawProcessing ? 'Processing…' : 'Withdraw'}
          </button>
        </div>
      )}

      {/* PORTFOLIO VIEW */}
      {currentView === 'transfer' && (() => {
        const spotBal = balance?.balances || {};
        const fromSpot = transferDir === 'to_trade';
        const srcBal = fromSpot ? spotBal : tradeBalances;
        const avail = Number(srcBal?.[transferToken] || 0);
        const price = tokens?.[transferToken]?.sell?.price ?? 0;
        const amtNum = parseFloat(transferAmount) || 0;
        const canSend = !!transferToken && amtNum > 0 && amtNum <= avail + 1e-12;
        const sideMeta = (isSpot) => (isSpot ? { name: 'Spot', Icon: FiCreditCard } : { name: 'Trade', Icon: FiTrendingUp });
        const fromM = sideMeta(fromSpot);
        const toM = sideMeta(!fromSpot);
        const srcTokens = Object.keys(srcBal || {}).filter((k) => (srcBal[k] || 0) > 0);
        const tokenList = srcTokens.length ? srcTokens : ['USDT'];
        return (
          <div style={{ minHeight: 380, animation: 'slideIn 0.42s cubic-bezier(0.22,1,0.36,1) both' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 40, marginBottom: 16 }}>
              <button aria-label="Back" onClick={() => setCurrentView('portfolio')} style={{ position: 'absolute', left: 0, background: 'none', border: 'none', color: '#9AA0AE', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 6, transition: 'color 0.2s ease' }} onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }} onMouseLeave={(e) => { e.currentTarget.style.color = '#9AA0AE'; }}>
                <FiArrowLeft size={22} />
              </button>
              <span style={{ color: '#fff', fontSize: 17, fontWeight: 700, letterSpacing: '0.2px' }}>Transfer</span>
            </div>

            {/* From -> To с кнопкой разворота */}
            <div style={{ position: 'relative', display: 'flex', gap: 10, marginBottom: 14 }}>
              {[{ side: 'From', m: fromM }, { side: 'To', m: toM }].map(({ side, m }) => (
                <div key={side} style={{ flex: 1, background: 'rgba(255,255,255,0.03)', border: 'none', borderRadius: 14, padding: '12px 14px' }}>
                  <div style={{ color: '#7E8AA0', fontSize: 11, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{side}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <span style={{ width: 30, height: 30, borderRadius: 9, background: 'rgba(96,189,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><m.Icon size={16} color="#60BDFF" /></span>
                    <span style={{ color: '#fff', fontSize: 15, fontWeight: 700 }}>{m.name}</span>
                  </div>
                </div>
              ))}
              <button onClick={() => { setTransferDir((d) => (d === 'to_trade' ? 'to_spot' : 'to_trade')); setTransferError(''); setTransferSuccess(''); }} aria-label="Swap direction" style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: 38, height: 38, borderRadius: '50%', background: '#1A1A1F', border: '1px solid #3A3A46', color: '#60BDFF', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.4)', transition: 'transform 0.25s ease' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translate(-50%,-50%) rotate(180deg)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translate(-50%,-50%) rotate(0deg)'; }}>
                <FiRepeat size={16} />
              </button>
            </div>

            {/* Сумма + токен */}
            <div className="df-field" style={{ ...dfField, padding: '10px 14px' }}>
              <div data-dd style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, position: 'relative' }}>
                <span style={{ ...dfLabel, marginBottom: 0 }}>Amount</span>
                <div className="df-pill" style={dfPill} onClick={() => toggleDD('tTok')}>
                  <img src={iconPath(transferToken)} alt={transferToken} style={{ width: 20, height: 20, borderRadius: '50%' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  <span style={{ color: '#EAF6FF', fontWeight: 600, fontSize: 13 }}>{transferToken}</span>
                  <FiChevronRight size={14} color="#60BDFF" style={{ transform: activeDD === 'tTok' ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s ease' }} />
                </div>
                {activeDD === 'tTok' && (
                  <div className="df-dd" style={{ ...dfDropdown, left: 'auto', right: 0, top: 'calc(100% + 6px)' }}>
                    {tokenList.map((key) => (
                      <div key={key} className="df-opt" style={dfOpt} onClick={() => { setTransferToken(key); setActiveDD(null); setTransferAmount(''); }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <img src={iconPath(key)} style={{ width: 26, height: 26, borderRadius: '50%' }} alt={key} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                          <span style={{ fontWeight: 600 }}>{key}</span>
                        </div>
                        <span style={{ fontSize: 12, color: '#60BDFF' }}>{formatNumber(srcBal[key] || 0)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 10 }}>
                <input className="df-input" type="text" placeholder="0" value={transferAmount} onChange={(e) => setTransferAmount(sanitizeAmount(e.target.value))} inputMode="decimal" style={{ ...dfInput, fontSize: 24, fontWeight: 600, width: '60%' }} />
                <span style={{ color: '#7E8190', fontSize: 13, whiteSpace: 'nowrap' }}>~${(amtNum * price).toFixed(2)}</span>
              </div>
              <div style={{ marginTop: 4, fontSize: 12 }}>
                <span style={{ color: '#7E8190' }}>{fromM.name} balance: </span>
                <span onClick={() => { if (avail > 0) setTransferAmount(String(avail)); }} title="Use full balance" style={{ color: '#60BDFF', fontWeight: 600, cursor: 'pointer', userSelect: 'none', transition: 'opacity 0.2s ease' }} onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.7'; }} onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}>
                  {formatNumber(avail)} {transferToken}
                </span>
              </div>
            </div>

            {transferError && (<div style={{ background: 'rgba(255,122,122,0.1)', border: '1px solid rgba(255,122,122,0.35)', borderRadius: 12, padding: '11px 14px', marginBottom: 12, color: '#FF7A7A', fontSize: 13.5, textAlign: 'center' }}>{transferError}</div>)}
            {transferSuccess && (<div style={{ background: 'rgba(63,209,139,0.1)', border: '1px solid rgba(63,209,139,0.35)', borderRadius: 12, padding: '11px 14px', marginBottom: 12, color: '#3FD18B', fontSize: 13.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, animation: 'fadeIn 0.3s ease' }}><FiCheck size={18} />{transferSuccess}</div>)}

            <button className="df-send" onClick={submitTransfer} disabled={!canSend || transferProcessing} style={{ width: '100%', backgroundColor: (!canSend || transferProcessing) ? '#2A2A33' : '#60BDFF', border: 'none', borderRadius: 14, padding: '14px 20px', height: 50, color: (!canSend || transferProcessing) ? '#6A6A78' : '#06121E', fontSize: 15, fontWeight: 700, cursor: (!canSend || transferProcessing) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 4 }}>
              <FiRepeat size={18} />
              {transferProcessing ? 'Processing…' : `Transfer to ${toM.name}`}
            </button>
          </div>
        );
      })()}

      {currentView === 'trade' && (
        <div style={{ minHeight: 380, animation: 'viewIn 0.42s cubic-bezier(0.22,1,0.36,1) both' }}>
          {/* Nav */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 40, marginBottom: 14 }}>
            <button aria-label="Back" onClick={() => setCurrentView('swap')} style={{ position: 'absolute', left: 0, background: 'none', border: 'none', color: '#9AA0AE', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 6, transition: 'color 0.2s ease' }} onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }} onMouseLeave={(e) => { e.currentTarget.style.color = '#9AA0AE'; }}>
              <FiArrowLeft size={22} />
            </button>
            <span style={{ color: '#fff', fontSize: 17, fontWeight: 700, letterSpacing: '0.2px' }}>Trade</span>
          </div>
          <div style={{ marginBottom: 12 }}>
            <TradeChart
              tokens={tokens}
              defaultSymbol="BTC"
              onSymbolChange={setTradeSymbol}
              onPriceUpdate={setTradePrice}
            />
          </div>
          <TradePanel
            userId={user_id}
            symbol={tradeSymbol}
            price={tradePrice}
            onAfterTrade={() => { fetchWallet(); try { wsBalance && wsBalance.send('get'); } catch {} }}
          />
        </div>
      )}

      {currentView === 'portfolio' && (
        <div style={{ minHeight: 380, animation: 'viewIn 0.42s cubic-bezier(0.22,1,0.36,1) both' }}>
          {/* Шапка: Back + заголовок Wallet по центру */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 40, marginBottom: 12 }}>
            <button
              onClick={() => setCurrentView('swap')}
              aria-label="Back"
              style={{ position: 'absolute', left: 0, background: 'none', border: 'none', color: '#9AA0AE', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 6, transition: 'color 0.2s ease' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#9AA0AE'; }}
            >
              <FiArrowLeft size={22} />
            </button>
            <span style={{ color: '#fff', fontSize: 17, fontWeight: 700, letterSpacing: '0.2px' }}>Wallet</span>
          </div>

          {/* Баланс с переключателем Total / Spot / Trade */}
          {(() => {
            const tabs = [
              { key: 'total', label: t('bal.total') },
              { key: 'spot', label: t('bal.spot') },
              { key: 'trade', label: t('bal.trade') },
            ];
            const values = { total: totalBalanceUsd + tradeBalanceUsd, spot: totalBalanceUsd, trade: tradeBalanceUsd };
            const val = values[balanceTab] || 0;
            return (
              <div style={{
                position: 'relative',
                background: 'transparent',
                borderRadius: 20,
                padding: '14px 18px 20px',
                marginBottom: 16,
                border: '1px solid rgba(96, 189, 255, 0.18)',
                boxShadow: '0 8px 28px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255,255,255,0.04)',
                overflow: 'hidden',
                animation: 'walletIn 0.45s cubic-bezier(0.22,1,0.36,1) both',
              }}>
                <div style={{ position: 'absolute', top: -40, right: -30, width: 150, height: 150, borderRadius: '50%', background: 'radial-gradient(circle, rgba(96,189,255,0.18) 0%, rgba(96,189,255,0) 70%)', pointerEvents: 'none' }} />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div style={{ display: 'inline-flex', background: 'rgba(0,0,0,0.25)', borderRadius: 11, padding: 3, gap: 2 }}>
                    {tabs.map((t) => {
                      const on = balanceTab === t.key;
                      return (
                        <button
                          key={t.key}
                          onClick={() => setBalanceTab(t.key)}
                          style={{ border: 'none', cursor: 'pointer', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 600, background: on ? 'rgba(96,189,255,0.20)' : 'transparent', color: on ? '#EAF6FF' : '#7E8AA0', transition: 'background 0.2s ease, color 0.2s ease' }}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                  <button onClick={() => setHideBalance(v => !v)} aria-label="Toggle balance visibility" style={{ background: 'none', border: 'none', color: '#9AB3C8', cursor: 'pointer', display: 'flex', padding: 4 }}>
                    {hideBalance ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                  <div style={{ color: '#FFFFFF', fontSize: 32, fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.05 }}>
                    {hideBalance ? '••••••' : '$' + val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/,/g, ' ')}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Компактные кнопки действий */}
          {(() => {
            const actions = [
              { key: 'deposit', label: t('wallet.deposit'), Icon: FiArrowDownLeft, onClick: handleDepositClick, acc: '#3FD18B' },
              { key: 'withdraw', label: t('wallet.withdraw'), Icon: FiArrowUpRight, onClick: handleWithdrawClick, acc: '#FF7A7A' },
              { key: 'transfer', label: t('wallet.transfer'), Icon: FiRepeat, onClick: handleTransferClick, acc: '#60BDFF' },
            ];
            return (
              <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
                {actions.map(({ key, label, Icon, onClick, acc }, idx) => (
                  <button
                    key={key}
                    onClick={onClick}
                    className="wallet-action"
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 7,
                      borderRadius: 13,
                      padding: '12px 6px',
                      color: '#E7E7EE',
                      cursor: 'pointer',
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid #26262E',
                      animationDelay: `${idx * 0.06}s`,
                      ['--acc']: acc,
                      ['--acc-bg']: `${acc}14`,
                      ['--acc-bg2']: `${acc}24`,
                    }}
                  >
                    <span className="wa-ic" style={{ display: 'flex' }}><Icon size={17} color={acc} /></span>
                    <span style={{ fontSize: 12.5, fontWeight: 600 }}>{label}</span>
                  </button>
                ))}
              </div>
            );
          })()}

          {/* Заголовок секции активов */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, padding: '0 2px' }}>
            <span style={{ color: '#fff', fontSize: 15, fontWeight: 700 }}>{t('wallet.assets')}</span>
            <button
              onClick={() => { setShowAllHistory(true); setHistoryLimit(7); fetchHistory(); }}
              style={{ background: 'none', border: 'none', color: '#60BDFF', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, padding: 0 }}
            >
              <FiClock size={13} /> {t('wallet.history')}
            </button>
          </div>

          {/* Список токенов (тап → деталка) */}
          <div style={{ maxHeight: 'calc(100vh - 430px)', minHeight: 120, overflowY: 'auto', paddingRight: 2, margin: '0 -2px' }}>
            {!balance || !tokens ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    backgroundColor: '#1F1F25', border: '1px solid #2E2E3A', borderRadius: 14,
                    padding: '12px 14px', animation: 'pulse 1.5s infinite',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: '#3E3E4B' }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ width: 60, height: 14, borderRadius: 4, backgroundColor: '#3E3E4B' }} />
                        <div style={{ width: 80, height: 11, borderRadius: 4, backgroundColor: '#3E3E4B' }} />
                      </div>
                    </div>
                    <div style={{ width: 64, height: 14, borderRadius: 4, backgroundColor: '#3E3E4B' }} />
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(() => {
                  // Курсы: spot — по swap (tokens.json), trade — по курсу трейда (tradePrices)
                  const spotPrice = (key) => tokens?.[key]?.sell?.price ?? 0;
                  const tradePrice = (key) => tradePrices?.[key] ?? tokens?.[key]?.sell?.price ?? 0;
                  const spotBal = balance?.balances || {};
                  const tradeBal = tradeBalances || {};
                  let tokenList;
                  if (balanceTab === 'spot') {
                    // Только спот-токены, оценка по курсу swap
                    tokenList = Object.entries(spotBal)
                      .filter(([key, val]) => val > 0 || key === 'USDT')
                      .map(([key, val]) => ({ currency: key, amount: val || 0, price: spotPrice(key), usdValue: (val || 0) * spotPrice(key), icon: iconPath(key) }));
                    if (!tokenList.length) tokenList = [{ currency: 'USDT', amount: 0, price: 1, usdValue: 0, icon: iconPath('USDT') }];
                  } else if (balanceTab === 'trade') {
                    // Только трейд-токены, оценка по курсу трейда
                    tokenList = Object.entries(tradeBal)
                      .filter(([, val]) => val > 0)
                      .map(([key, val]) => ({ currency: key, amount: val || 0, price: tradePrice(key), usdValue: (val || 0) * tradePrice(key), icon: iconPath(key) }));
                    if (!tokenList.length) tokenList = [{ currency: 'USDT', amount: 0, price: 1, usdValue: 0, icon: iconPath('USDT'), empty: true }];
                  } else {
                    // Total: объединяем спот+трейд; USD = spot*swap + trade*trade
                    const keys = new Set([...Object.keys(spotBal), ...Object.keys(tradeBal)]);
                    tokenList = [...keys].map((key) => {
                      const sa = spotBal[key] || 0;
                      const ta = tradeBal[key] || 0;
                      const amount = sa + ta;
                      const usdValue = sa * spotPrice(key) + ta * tradePrice(key);
                      return { currency: key, amount, price: amount > 0 ? usdValue / amount : spotPrice(key), usdValue, icon: iconPath(key) };
                    }).filter((t) => t.amount > 0 || t.currency === 'USDT');
                    if (!tokenList.length) tokenList = [{ currency: 'USDT', amount: 0, price: 1, usdValue: 0, icon: iconPath('USDT') }];
                  }
                  // Вручную включённые токены без баланса — показываем с нулём.
                  const priceFor = (key) => (balanceTab === 'trade' ? tradePrice(key) : spotPrice(key));
                  const present = new Set(tokenList.map((t) => String(t.currency).toUpperCase()));
                  for (const sym of extraTokens) {
                    const S = String(sym).toUpperCase();
                    if (S === 'USDT' || present.has(S)) continue;
                    tokenList.push({ currency: S, amount: 0, price: priceFor(S), usdValue: 0, icon: iconPath(S) });
                    present.add(S);
                  }
                  // USDT обязателен в списке (даже с нулевым балансом)
                  if (!present.has('USDT')) {
                    tokenList.push({ currency: 'USDT', amount: 0, price: 1, usdValue: 0, icon: iconPath('USDT') });
                  }
                  // Сортировка: USDT всегда первый, остальные по убыванию стоимости
                  tokenList.sort((a, b) => {
                    const au = String(a.currency).toUpperCase() === 'USDT';
                    const bu = String(b.currency).toUpperCase() === 'USDT';
                    if (au && !bu) return -1;
                    if (bu && !au) return 1;
                    return (b.usdValue || 0) - (a.usdValue || 0);
                  });
                  // скрыть мелкие балансы (< $1), кроме USDT
                  if (hideSmall) {
                    tokenList = tokenList.filter((tk) => String(tk.currency).toUpperCase() === 'USDT' || (tk.usdValue || 0) >= 1);
                  }
                  tokenList = tokenList.slice(0, 5);   // максимум 5 токенов в списке

                  return tokenList.map((token, index) => {
                    const usdValue = token.usdValue != null ? token.usdValue : token.amount * token.price;
                    return (
                      <div
                        key={token.currency}
                        onClick={() => openTokenSheet(token)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          backgroundColor: 'rgba(31,31,37,0.55)', border: '1px solid rgba(46,46,58,0.7)', borderRadius: 14,
                          backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
                          padding: '11px 12px', cursor: 'pointer',
                          transition: 'background-color 0.2s ease, border-color 0.2s ease, transform 0.12s ease',
                          animation: `rowIn 0.3s ease ${index * 0.04}s both`,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(40,40,50,0.65)'; e.currentTarget.style.borderColor = '#3E4A5A'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(31,31,37,0.55)'; e.currentTarget.style.borderColor = 'rgba(46,46,58,0.7)'; }}
                        onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.99)'; }}
                        onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                          <img src={token.icon} alt={token.currency}
                            style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0 }}
                            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                            <span style={{ color: '#FFFFFF', fontWeight: 600, fontSize: 15 }}>{token.currency}</span>
                            <span style={{ color: '#7E7E95', fontSize: 12.5, fontWeight: 500 }}>
                              {hideBalance ? '••••' : (token.amount > 0 ? formatNumber(token.amount) : '0.00')}
                            </span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ color: '#FFFFFF', fontWeight: 600, fontSize: 15, marginBottom: 2 }}>
                              {hideBalance ? '••••' : '$' + usdValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/,/g, ' ')}
                            </div>
                            <div style={{ color: '#7E7E95', fontSize: 11.5, fontWeight: 500 }}>${formatNumber(token.price)}</div>
                          </div>
                          <FiChevronRight size={18} color="#5A5A6A" style={{ flexShrink: 0 }} />
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bottom-sheet: деталка токена (тап по токену в Wallet) */}
      {selectedToken && (() => {
        const sym = selectedToken.currency;
        const price = tokens?.[sym]?.sell?.price ?? selectedToken.price ?? 0;
        const amount = selectedToken.amount ?? (balance?.balances?.[sym] ?? 0);
        const usd = amount * price;
        const list = tokenHistory(sym);
        const acts = [
          { key: 'deposit', label: t('wallet.deposit'), Icon: FiArrowDownLeft, acc: '#3FD18B' },
          { key: 'withdraw', label: t('wallet.withdraw'), Icon: FiArrowUpRight, acc: '#FF7A7A' },
          { key: 'transfer', label: t('wallet.transfer'), Icon: FiRepeat, acc: '#60BDFF' },
        ];
        return (
          <div
            onClick={(e) => { if (e.target === e.currentTarget) closeTokenSheet(); }}
            style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(4,8,12,0.55)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', animation: 'sheetFade 0.2s ease' }}
          >
            <div style={{ width: '100%', maxWidth: 480, maxHeight: '86vh', ...GLASS_PANEL, borderBottom: 'none', borderTopLeftRadius: 26, borderTopRightRadius: 26, boxShadow: '0 -18px 50px rgba(0,0,0,0.55)', display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: 'sheetUp 0.3s cubic-bezier(0.22,1,0.36,1)' }}>
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10 }}>
                <div style={{ width: 42, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.22)' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px 6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <img src={selectedToken.icon || iconPath(sym)} alt={sym} style={{ width: 44, height: 44, borderRadius: '50%', boxShadow: '0 0 0 3px rgba(96,189,255,0.10), 0 6px 18px rgba(96,189,255,0.18)' }} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>{sym}</div>
                    <div style={{ color: '#7E7E95', fontSize: 13, fontWeight: 500 }}>{tokens?.[sym]?.title || sym}</div>
                  </div>
                </div>
                <button onClick={closeTokenSheet} aria-label="Close" style={{ width: 34, height: 34, borderRadius: 12, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FiX size={18} />
                </button>
              </div>
              <div style={{ padding: '8px 18px 14px' }}>
                <div style={{ background: 'linear-gradient(135deg, rgba(96,189,255,0.11), rgba(255,255,255,0.03) 58%)', border: '1px solid rgba(96,189,255,0.16)', borderRadius: 18, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: '#7E7E95', fontSize: 12, marginBottom: 4 }}>Balance</div>
                    <div style={{ color: '#fff', fontSize: 19, fontWeight: 700 }}>{formatNumber(amount)} <span style={{ color: '#7E7E95', fontSize: 13, fontWeight: 600 }}>{sym}</span></div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#7E7E95', fontSize: 12, marginBottom: 4 }}>≈ USD</div>
                    <div style={{ color: '#fff', fontSize: 19, fontWeight: 700 }}>${usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/,/g, ' ')}</div>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, padding: '0 18px 16px' }}>
                {acts.map(({ key, label, Icon, acc }) => (
                  <button
                    key={key}
                    onClick={() => tokenAction(key, sym)}
                    className="wallet-action"
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, borderRadius: 13, padding: '12px 6px', color: '#E7E7EE', cursor: 'pointer', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)', ['--acc']: acc, ['--acc-bg']: `${acc}14`, ['--acc-bg2']: `${acc}24` }}
                  >
                    <span className="wa-ic" style={{ display: 'flex' }}><Icon size={17} color={acc} /></span>
                    <span style={{ fontSize: 12.5, fontWeight: 600 }}>{label}</span>
                  </button>
                ))}
              </div>
              <div style={{ padding: '14px 18px 8px', backgroundImage: GLASS_HAIRLINE, backgroundSize: 'calc(100% - 36px) 1px', backgroundPosition: 'top center', backgroundRepeat: 'no-repeat' }}>
                <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>{sym} history</span>
              </div>
              <div style={{ overflowY: 'auto', padding: '0 18px 22px', flex: 1 }}>
                {historyLoading ? (
                  <div style={{ color: '#7E7E95', fontSize: 13, textAlign: 'center', padding: '18px 0' }}>Loading…</div>
                ) : list.length === 0 ? (
                  <div style={{ color: '#6A6A78', fontSize: 13, textAlign: 'center', padding: '26px 0' }}>No operations with {sym} yet</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {list.map((tx, i) => {
                      const m = txMeta(tx);
                      return (
                        <div key={tx.id || i} onClick={() => setSelectedTx(tx)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 13, padding: '10px 12px', cursor: 'pointer', animation: `rowIn 0.25s ease ${i * 0.03}s both` }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                            <div style={{ width: 34, height: 34, borderRadius: 10, background: `${m.color}1A`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <m.Icon size={16} color={m.color} />
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ color: '#fff', fontSize: 13.5, fontWeight: 600 }}>{m.label}</div>
                              <div style={{ color: '#6A6A78', fontSize: 11.5 }}>{m.sub || formatTxDate(tx.date_time)}</div>
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <div style={{ color: m.color, fontSize: 13.5, fontWeight: 700 }}>{m.sign}{formatNumber(m.value)} {m.asset}</div>
                            <div style={{ color: '#6A6A78', fontSize: 11 }}>{formatTxDate(tx.date_time)}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Bottom-sheet: вся история операций (Activity) */}
      {showAllHistory && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowAllHistory(false); }}
          style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', animation: 'sheetFade 0.2s ease' }}
        >
          <div style={{ width: '100%', maxWidth: 480, maxHeight: '86vh', background: '#16161B', borderTopLeftRadius: 24, borderTopRightRadius: 24, border: '1px solid #2A2A33', boxShadow: '0 -10px 40px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: 'sheetUp 0.3s cubic-bezier(0.22,1,0.36,1)' }}>
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10 }}>
              <div style={{ width: 42, height: 5, borderRadius: 3, background: '#3A3A46' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 11, background: 'rgba(96,189,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FiClock size={18} color="#60BDFF" />
                </div>
                <span style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>Activity</span>
              </div>
              <button onClick={() => setShowAllHistory(false)} aria-label="Close" style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid #2E2E3A', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiX size={18} />
              </button>
            </div>
            <div style={{ overflowY: 'auto', padding: '0 18px 24px', flex: 1 }}>
              {historyLoading && history === null ? (
                <div style={{ color: '#7E7E95', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>Loading…</div>
              ) : !Array.isArray(history) || history.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '40px 0', color: '#6A6A78' }}>
                  <FiClock size={34} color="#3A3A46" />
                  <div style={{ fontSize: 14 }}>No activity yet</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {history.slice(0, historyLimit).map((tx, i) => {
                    const m = txMeta(tx);
                    return (
                      <div key={tx.id || i} onClick={() => setSelectedTx(tx)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,0.02)', border: '1px solid #23232B', borderRadius: 12, padding: '11px 13px', cursor: 'pointer', animation: `rowIn 0.25s ease ${Math.min(i, 12) * 0.025}s both` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                          <div style={{ width: 36, height: 36, borderRadius: 11, background: `${m.color}1A`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <m.Icon size={17} color={m.color} />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{m.label}</div>
                            <div style={{ color: '#6A6A78', fontSize: 11.5 }}>{m.sub || formatTxDate(tx.date_time)}</div>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <div style={{ color: m.color, fontSize: 14, fontWeight: 700 }}>{m.sign}{formatNumber(m.value)} {m.asset}</div>
                          <div style={{ color: '#6A6A78', fontSize: 11 }}>{formatTxDate(tx.date_time)}</div>
                        </div>
                      </div>
                    );
                  })}
                  {history.length > historyLimit && (
                    <button
                      onClick={() => setHistoryLimit((l) => l + 7)}
                      style={{ marginTop: 4, width: '100%', background: 'rgba(96,189,255,0.08)', border: '1px solid rgba(96,189,255,0.18)', color: '#60BDFF', borderRadius: 12, padding: '11px', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s ease' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(96,189,255,0.15)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(96,189,255,0.08)'; }}
                    >
                      Show more ({history.length - historyLimit})
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Модалка управления отображением токенов (Manage) */}
      {manageOpen && (() => {
        const allSyms = Array.from(new Set([
          'USDT',
          ...Object.keys(tokens || {}).filter((k) => k !== 'trading-pairs'),
          ...Object.keys(balance?.balances || {}),
        ].map((s) => String(s).toUpperCase())));
        allSyms.sort((a, b) => (a === 'USDT' ? -1 : b === 'USDT' ? 1 : a.localeCompare(b)));
        const locked = lockedTokenSet(); // USDT + токены с балансом — выключить нельзя
        return (
          <div onClick={(e) => { if (e.target === e.currentTarget) setManageOpen(false); }}
            style={{ position: 'fixed', inset: 0, zIndex: 3200, background: 'rgba(0,0,0,0.66)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', animation: 'sheetFade 0.2s ease' }}>
            <div style={{ position: 'relative', width: '100%', maxWidth: 480, maxHeight: '86vh', background: '#16161B', borderTopLeftRadius: 24, borderTopRightRadius: 24, border: '1px solid #2A2A33', boxShadow: '0 -10px 40px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: 'sheetUp 0.3s cubic-bezier(0.22,1,0.36,1)' }}>
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10 }}>
                <div style={{ width: 42, height: 5, borderRadius: 3, background: '#3A3A46' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px 8px' }}>
                <span style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>{t('manage.title')}</span>
                <button onClick={() => setManageOpen(false)} aria-label="Close" style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid #2E2E3A', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FiX size={18} />
                </button>
              </div>
              <div style={{ color: '#7E7E95', fontSize: 12.5, padding: '0 18px 10px' }}>{t('manage.hint')}</div>
              <div style={{ overflowY: 'auto', padding: '0 18px 24px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {allSyms.map((sym) => {
                  const isUSDT = sym === 'USDT';
                  const isLocked = locked.has(sym);            // USDT или есть баланс → нельзя выключить
                  const visible = isLocked || extraTokens.has(sym); // виден, если залочен или вручную включён
                  const note = isUSDT ? `• ${t('manage.alwaysOn')}` : (isLocked ? `• ${t('manage.hasBalance')}` : '');
                  return (
                    <div key={sym} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,0.02)', border: '1px solid #23232B', borderRadius: 12, padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                        <img src={iconPath(sym)} alt={sym} style={{ width: 32, height: 32, borderRadius: '50%' }} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{sym}{note && <span style={{ color: '#7E7E95', fontSize: 11, marginLeft: 6 }}>{note}</span>}</div>
                          <div style={{ color: '#7E7E95', fontSize: 11.5 }}>{tokens?.[sym]?.title || ''}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleTokenExtra(sym)}
                        disabled={isLocked}
                        aria-label="toggle"
                        style={{ width: 44, height: 26, borderRadius: 13, border: 'none', position: 'relative', cursor: isLocked ? 'not-allowed' : 'pointer', background: visible ? '#3FD18B' : '#3A3A46', opacity: isLocked ? 0.6 : 1, transition: 'background 0.2s ease', flexShrink: 0 }}
                      >
                        <span style={{ position: 'absolute', top: 3, left: visible ? 21 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s ease' }} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Модалка деталей транзакции (клик по строке истории) */}
      {selectedTx && (() => {
        const tx = selectedTx;
        const m = txMeta(tx);
        const exUrl = explorerTxUrl(tx.network, tx.tx_hash);
        const Row = ({ label, children }) => (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '11px 0', borderBottom: '1px solid #20202A' }}>
            <span style={{ color: '#7E7E95', fontSize: 13 }}>{label}</span>
            <span style={{ color: '#fff', fontSize: 13.5, fontWeight: 600, textAlign: 'right', wordBreak: 'break-all' }}>{children}</span>
          </div>
        );
        return (
          <div onClick={(e) => { if (e.target === e.currentTarget) setSelectedTx(null); }}
            style={{ position: 'fixed', inset: 0, zIndex: 3200, background: 'rgba(0,0,0,0.66)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', animation: 'sheetFade 0.2s ease' }}>
            <div style={{ position: 'relative', width: '100%', maxWidth: 480, maxHeight: '88vh', background: '#16161B', borderTopLeftRadius: 24, borderTopRightRadius: 24, border: '1px solid #2A2A33', boxShadow: '0 -10px 40px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: 'sheetUp 0.3s cubic-bezier(0.22,1,0.36,1)' }}>
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10 }}>
                <div style={{ width: 42, height: 5, borderRadius: 3, background: '#3A3A46' }} />
              </div>
              <button onClick={() => setSelectedTx(null)} aria-label="Close" style={{ position: 'absolute', top: 16, right: 16, width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid #2E2E3A', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                <FiX size={18} />
              </button>
              {/* Шапка: иконка + сумма + статус */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '14px 18px 6px' }}>
                <div style={{ width: 56, height: 56, borderRadius: 18, background: `${m.color}1A`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                  <m.Icon size={26} color={m.color} />
                </div>
                <div style={{ color: '#fff', fontSize: 15, fontWeight: 700 }}>{m.label}</div>
                <div style={{ color: m.color, fontSize: 24, fontWeight: 800, marginTop: 4 }}>{m.sign}{formatNumber(m.value)} {m.asset}</div>
                <div style={{ marginTop: 8, padding: '4px 12px', borderRadius: 20, background: `${m.color}18`, color: m.color, fontSize: 12.5, fontWeight: 700 }}>{m.statusLabel}</div>
              </div>
              {/* Детали */}
              <div style={{ overflowY: 'auto', padding: '8px 18px 24px' }}>
                {m.kind === 'transfer' ? (
                  <Row label="Direction">{tx.direction === 'to_trade' ? 'Spot → Trade' : 'Trade → Spot'}</Row>
                ) : m.kind === 'swap' ? (
                  <Row label="Pair">{tx.give} → {tx.recipient}</Row>
                ) : null}
                {tx.network ? <Row label="Network">USDT {tx.network}</Row> : null}
                {tx.sender ? <Row label="From">{shortHash(tx.sender)}</Row> : null}
                {tx.confirmations != null ? <Row label="Confirmations">{tx.confirmations}</Row> : null}
                {tx.block_number != null ? <Row label="Block">{tx.block_number}</Row> : null}
                {tx.tx_hash ? (
                  <div style={{ padding: '11px 0', borderBottom: '1px solid #20202A' }}>
                    <div style={{ color: '#7E7E95', fontSize: 13, marginBottom: 6 }}>Transaction hash</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <code style={{ flex: 1, color: '#fff', fontFamily: 'monospace', fontSize: 12.5, wordBreak: 'break-all' }}>{shortHash(tx.tx_hash)}</code>
                      <button onClick={() => handleCopy(tx.tx_hash)} style={{ flexShrink: 0, width: 32, height: 32, borderRadius: 9, background: 'rgba(96,189,255,0.12)', border: '1px solid rgba(96,189,255,0.2)', color: '#60BDFF', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{copied ? <FiCheck size={15} /> : <FiCopy size={15} />}</button>
                    </div>
                    {exUrl ? <a href={exUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 8, color: '#60BDFF', fontSize: 12.5, fontWeight: 600, textDecoration: 'none' }}>View in explorer ↗</a> : null}
                  </div>
                ) : null}
                <Row label="Date">{formatTxDate(tx.date_time)}</Row>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Модальное окно для подтверждения транзакции */}
      {showModal && (
        <div
          className="fade-in"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && transactionStatus !== 'pending') {
              setShowModal(false);
              setTransactionStatus('pending');
              setTransactionError('');
              setIsProcessing(false);
            }
          }}
        >
          <div
            style={{
              background: 'linear-gradient(180deg, rgba(34,36,44,0.95) 0%, rgba(20,22,28,0.96) 100%)',
              backdropFilter: 'blur(22px)',
              WebkitBackdropFilter: 'blur(22px)',
              padding: 26,
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 22,
              maxWidth: 430,
              width: '100%',
              boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
              animation: 'modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {transactionStatus === 'pending' && (
              <div style={{ animation: 'fadeIn 0.3s ease' }}>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                  <h3 style={{ color: 'white', fontSize: 22, fontWeight: 600, marginBottom: 20 }}>
                    Confirm Transaction
                  </h3>
                  {isProcessing ? (
                    <div style={{ 
                      width: 80, 
                      height: 80, 
                      margin: '0 auto 20px',
                      borderRadius: '50%',
                      border: '3px solid #60BDFF',
                      borderTopColor: 'transparent',
                      animation: 'spin 1s linear infinite',
                    }} />
                  ) : (
                    <div style={{ 
                      width: 100, 
                      height: 100, 
                      margin: '0 auto 20px',
                      borderRadius: '50%',
                      border: '2px solid #60BDFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                    }}>
                      <svg
                        width="56"
                        height="56"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#60BDFF"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          animation: 'arrowGentle 2s ease-in-out infinite',
                        }}
                      >
                        <line x1="12" y1="18" x2="12" y2="5" />
                        <polyline points="8 11 12 5 16 11" />
                      </svg>
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ color: '#9CA3AF', fontSize: 14, marginBottom: 8, textAlign: 'left' }}>
                    Transaction Details:
                  </div>
                  <div style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 14,
                    padding: 16,
                    color: 'white',
                    fontSize: 15,
                    lineHeight: 1.6,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ color: '#9CA3AF' }}>You receive:</span>
                      <span style={{ fontWeight: 600 }}>{formatNumber(finalReceived)} {token2}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ color: '#9CA3AF' }}>You send:</span>
                      <span style={{ fontWeight: 600 }}>{formatNumber(displayAmount1)} {token1}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <span style={{ color: '#9CA3AF' }}>Fee:</span>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{formatFee(feeToken2)} {token2}</span>
                        <span style={{ fontWeight: 400, fontSize: 12, color: '#7E7E95' }}>({feeUsd.toFixed(2)}$)</span>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleConfirm}
                  disabled={isProcessing}
                  className="confirm-button"
                  style={{
                    padding: '10px 20px',
                    borderRadius: 16,
                    backgroundColor: '#FFFFFF',
                    border: 'none',
                    color: '#0A0E16',
                    fontSize: 16,
                    fontWeight: 700,
                    width: '100%',
                    height: 50,
                    cursor: isProcessing ? 'wait' : 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    opacity: isProcessing ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  {isProcessing ? (
                    'Processing...'
                  ) : !isSameToken && !isPairAllowed ? (
                    'The pair is not supported'
                  ) : (
                    <>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M5 12l5 5L20 7" />
                      </svg>
                      Swap
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setTransactionStatus('pending');
                    setTransactionError('');
                    setIsProcessing(false);
                  }}
                  className="confirm-button modal-ghost"
                  style={{
                    marginTop: 10,
                    padding: '10px 20px',
                    borderRadius: 16,
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#9CA3AF',
                    fontSize: 15,
                    fontWeight: 600,
                    width: '100%',
                    height: 48,
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  Cancel
                </button>
              </div>
            )}

            {transactionStatus === 'success' && (
              <div style={{ animation: 'modalSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                  <h3 style={{ color: 'white', fontSize: 22, fontWeight: 600, marginBottom: 12 }}>
                    Transaction completed
                  </h3>
                  <p style={{ color: '#3ECF8E', fontSize: 16, marginBottom: 24 }}>
                    You approved {token2}
                  </p>
                  <div style={{
                    width: 80,
                    height: 80,
                    margin: '0 auto',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #46E0A0 0%, #2FB87A 100%)',
                    boxShadow: '0 10px 30px rgba(62,207,142,0.35)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'iconScale 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}>
                    <svg
                      width="40"
                      height="40"
                      fill="none"
                      stroke="white"
                      strokeWidth="3"
                      viewBox="0 0 24 24"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setTransactionStatus('pending');
                    setTransactionError('');
                    setIsProcessing(false);
                  }}
                  className="confirm-button"
                  style={{
                    padding: '14px 20px',
                    borderRadius: 16,
                    backgroundColor: '#FFFFFF',
                    border: 'none',
                    color: '#0A0E16',
                    fontSize: 16,
                    fontWeight: 700,
                    width: '100%',
                    height: 50,
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="19" y1="12" x2="5" y2="12" />
                    <polyline points="12 19 5 12 12 5" />
                  </svg>
                  Back
                </button>
              </div>
            )}

            {transactionStatus === 'error' && (
              <div style={{ animation: 'modalSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                  <div style={{
                    width: 54,
                    height: 54,
                    flexShrink: 0,
                    borderRadius: 15,
                    background: 'rgba(96,189,255,0.10)',
                    border: '1px solid rgba(96,189,255,0.26)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'iconScale 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}>
                    <svg
                      width="26"
                      height="26"
                      fill="none"
                      stroke="#60BDFF"
                      strokeWidth="2.4"
                      viewBox="0 0 24 24"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </div>
                  <div>
                    <h3 style={{ color: '#F4F6FA', fontSize: 23, fontWeight: 700, letterSpacing: -0.4, margin: 0, lineHeight: 1.1 }}>
                      Transaction Error
                    </h3>
                    <p style={{ color: '#6E727C', fontSize: 13.5, margin: '4px 0 0' }}>
                      Failed to complete exchange
                    </p>
                  </div>
                </div>
                {transactionError && (
                  <div style={{
                    borderLeft: '4px solid #60BDFF',
                    background: 'rgba(96,189,255,0.06)',
                    borderRadius: '0 12px 12px 0',
                    padding: '14px 16px',
                    marginBottom: 24,
                    color: '#C2C8D2',
                    fontSize: 13.5,
                    lineHeight: 1.55,
                    textAlign: 'left',
                  }}>
                    {transactionError}
                  </div>
                )}
                <button
                  onClick={() => {
                    setShowModal(false);
                    setTransactionStatus('pending');
                    setTransactionError('');
                    setIsProcessing(false);
                  }}
                  className="confirm-button"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#60BDFF';
                    e.currentTarget.style.borderColor = '#60BDFF';
                    e.currentTarget.style.color = '#06121C';
                    e.currentTarget.style.boxShadow = '0 10px 26px rgba(96,189,255,0.28)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = 'rgba(96,189,255,0.30)';
                    e.currentTarget.style.color = '#E7EAF0';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  style={{
                    padding: '14px 20px',
                    borderRadius: 14,
                    background: 'transparent',
                    border: '1px solid rgba(96,189,255,0.30)',
                    color: '#E7EAF0',
                    fontSize: 15,
                    fontWeight: 600,
                    width: '100%',
                    height: 50,
                    cursor: 'pointer',
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="19" y1="12" x2="5" y2="12" />
                    <polyline points="12 19 5 12 12 5" />
                  </svg>
                  Back
                </button>
              </div>
            )}

            {/* Fallback если статус неизвестный */}
            {transactionStatus !== 'pending' && transactionStatus !== 'success' && transactionStatus !== 'error' && (
              <div style={{ textAlign: 'center', color: 'white' }}>
                <p>Unknown status: {transactionStatus}</p>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setTransactionStatus('pending');
                  }}
                  style={{
                    marginTop: 20,
                    padding: '14px 20px',
                    borderRadius: 15,
                    backgroundColor: '#60BDFF',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                  }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && toastMessage && (
        <div
          style={{
            position: 'fixed',
            top: 'calc(50% - 50px)',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10000,
            animation: showToast ? 'toastFadeIn 0.3s ease-out' : 'toastFadeOut 0.3s ease-in',
          }}
        >
          <div
            style={{
              backgroundColor: 'rgba(255, 96, 99, 0.15)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 96, 99, 0.3)',
              borderRadius: 12,
              padding: '14px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
              minWidth: 280,
            }}
          >
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                backgroundColor: '#FF6063',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
            <span style={{ color: 'white', fontSize: 15, fontWeight: 500 }}>
              {toastMessage}
            </span>
          </div>
        </div>
      )}

    </div>
  );
}

export default SwapBlock;
